import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Divider,
  Alert,
  Checkbox,
  Tooltip,
  InputAdornment,
  Switch,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import FindReplaceIcon from '@mui/icons-material/FindReplace';
import StorageIcon from '@mui/icons-material/Storage';
import DnsIcon from '@mui/icons-material/Dns';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import CloseIcon from '@mui/icons-material/Close';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import DownloadIcon from '@mui/icons-material/Download';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import MoreTimeIcon from '@mui/icons-material/MoreTime';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import TuneIcon from '@mui/icons-material/Tune';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import HistoryIcon from '@mui/icons-material/History';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import config from '../../config/config';
import handleOpenSweetAlert from '../../components/handleOpenSweetAlert';
import { useSession } from '../../components/hooks/useSession';

const elegantInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#ffffff',
    fontSize: 'clamp(0.70rem, 0.68rem + 0.10vw, 0.82rem)',
    '& fieldset': { borderColor: '#e2e8f0' },
    '&:hover fieldset': { borderColor: '#cbd5e1' },
    '&.Mui-focused fieldset': { borderColor: '#0655A5', borderWidth: '1.5px' },
  },
  '& .MuiInputLabel-root': {
    fontSize: 'clamp(0.70rem, 0.68rem + 0.10vw, 0.82rem)',
    fontWeight: 600
  },
};

const getEmpresaChipSx = (empresa = '') => {
  const norm = String(empresa).toUpperCase();
  if (norm.includes('OFFSHORE')) {
    return {
      bgcolor: '#e6f0fa',
      color: '#005eb8',
      borderColor: '#b8daff',
      fontSize: 'clamp(0.60rem, 0.58rem + 0.08vw, 0.72rem)',
      fontWeight: 700
    };
  }
  if (norm.includes('PESADO')) {
    return {
      bgcolor: '#fcefdc',
      color: '#6c3400',
      borderColor: '#f5d3a5',
      fontSize: 'clamp(0.60rem, 0.58rem + 0.08vw, 0.72rem)',
      fontWeight: 700
    };
  }
  if (norm.includes('ECO') || norm.includes('CONSTRUCC')) {
    return {
      bgcolor: '#f5ebd9',
      color: '#5c3317',
      borderColor: '#e2ccb2',
      fontSize: 'clamp(0.60rem, 0.58rem + 0.08vw, 0.72rem)',
      fontWeight: 700
    };
  }
  return {
    bgcolor: '#eef2f7',
    color: '#334155',
    borderColor: '#d9e2ec',
    fontSize: 'clamp(0.60rem, 0.58rem + 0.08vw, 0.72rem)',
    fontWeight: 700
  };
};

// Helpers sesión
const getSessionString = (key) => sessionStorage.getItem(key) || '';

const getSessionArray = (key) => {
  try {
    const value = sessionStorage.getItem(key);
    if (!value) return [];
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    console.error('Error parseando', key, e);
    return [];
  }
};

const normalizeEmpresaArray = (idEmpresa) => {
  if (idEmpresa == null) return [];
  if (Array.isArray(idEmpresa)) {
    return idEmpresa.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  }
  if (typeof idEmpresa === 'string') {
    const s = idEmpresa.trim();
    if (s.startsWith('[') && s.endsWith(']')) {
      try {
        const arr = JSON.parse(s);
        if (Array.isArray(arr)) {
          return arr.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
        }
      } catch {}
    }
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? [n] : [];
  }
  const n = Number(idEmpresa);
  return Number.isFinite(n) && n > 0 ? [n] : [];
};

export default function Funciones() {
  const apiUrl = config.apiBaseUrl;
  useSession();

  // ─── Datos de sesión reactivos ────────────────────────────────────────
  const [loggedInUsername, setLoggedInUsername] = useState(getSessionString('loggedInUsername'));
  const [idEmpresa, setIdEmpresa] = useState(getSessionArray('EmpresaId'));
  const [nombreEmpresa, setNombreEmpresa] = useState(getSessionString('EmpresaNombre'));

  const empresaIdsKey = useMemo(() => {
    const arr = normalizeEmpresaArray(idEmpresa);
    return arr.length ? arr.join(',') : '';
  }, [idEmpresa]);

  const empresaIds = useMemo(() => {
    if (!empresaIdsKey) return [];
    return empresaIdsKey
      .split(',')
      .map((n) => Number(n))
      .filter((x) => Number.isFinite(x) && x > 0);
  }, [empresaIdsKey]);

  useEffect(() => {
    const handleEmpresaChange = () => {
      const nuevosIds = getSessionArray('EmpresaId');
      const nuevoUser = getSessionString('loggedInUsername');
      const nuevoNombre = getSessionString('EmpresaNombre');
      setIdEmpresa(nuevosIds);
      setLoggedInUsername(nuevoUser);
      setNombreEmpresa(nuevoNombre);
    };

    window.addEventListener('empresaCambiada', handleEmpresaChange);
    return () => window.removeEventListener('empresaCambiada', handleEmpresaChange);
  }, []);

  // Estados de empresas activas
  const [empresasDisponibles, setEmpresasDisponibles] = useState([]);
  const [idEmpresaSelected, setIdEmpresaSelected] = useState('');

  // 1. Auditoría Canceladas
  const [modalAuditoriaOpen, setModalAuditoriaOpen] = useState(false);
  const [modoAuditoria, setModoAuditoria] = useState('rango'); // 'rango' | 'folio'
  const [folioAuditar, setFolioAuditar] = useState('');
  const [fechaInicioAuditar, setFechaInicioAuditar] = useState('');
  const [fechaFinAuditar, setFechaFinAuditar] = useState('');
  const [loadingAuditoria, setLoadingAuditoria] = useState(false);
  const [resultadoAuditoria, setResultadoAuditoria] = useState(null);
  const [selectedAuditoriaIds, setSelectedAuditoriaIds] = useState(new Set());
  const [aplicandoCancelaciones, setAplicandoCancelaciones] = useState(false);

  // 2. Respaldo BD / Estadísticas / Descargas
  const [modalTablasOpen, setModalTablasOpen] = useState(false);
  const [tablasBD, setTablasBD] = useState([]);
  const [loadingTablas, setLoadingTablas] = useState(false);
  const [modalRespaldosOpen, setModalRespaldosOpen] = useState(false);
  const [listaRespaldos, setListaRespaldos] = useState([]);
  const [loadingRespaldos, setLoadingRespaldos] = useState(false);
  const [generandoRespaldoDB, setGenerandoRespaldoDB] = useState(false);
  const [generandoRespaldoArchivos, setGenerandoRespaldoArchivos] = useState(false);
  const [diasRetencion, setDiasRetencion] = useState(() => {
    const saved = localStorage.getItem('preferencia_retencion_dias');
    return saved ? Number(saved) : 7;
  });
  const [limpiandoAntiguos, setLimpiandoAntiguos] = useState(false);

  const handleCambiarDiasRetencion = (nuevosDias) => {
    const d = Number(nuevosDias);
    setDiasRetencion(d);
    localStorage.setItem('preferencia_retencion_dias', String(d));
  };

  // 3. Estado de Sincronización y Auto-Mantenimiento
  const [estadoSync, setEstadoSync] = useState(null);
  const [loadingEstadoSync, setLoadingEstadoSync] = useState(false);
  const [ejecutandoAutoMant, setEjecutandoAutoMant] = useState(false);

  // 3.1 Base de Datos Espejo / Failover
  const [modalEspejoOpen, setModalEspejoOpen] = useState(false);
  const [estadoEspejo, setEstadoEspejo] = useState(null);
  const [loadingEspejo, setLoadingEspejo] = useState(false);
  const [sincronizandoEspejo, setSincronizandoEspejo] = useState(false);
  const [resultadoSyncEspejo, setResultadoSyncEspejo] = useState(null);

  // 4. Gestor de Tareas Programadas (Rutinas Cron)
  const [modalTareasOpen, setModalTareasOpen] = useState(false);
  const [listaTareas, setListaTareas] = useState([]);
  const [loadingTareas, setLoadingTareas] = useState(false);
  const [modalEditarTareaOpen, setModalEditarTareaOpen] = useState(false);
  const [tareaEnEdicion, setTareaEnEdicion] = useState(null);
  const [formTarea, setFormTarea] = useState({
    nombre: '',
    accion: 'RESPALDO_BD',
    tipoFrecuencia: 'DIARIO',
    horaFija: '03:00',
    diaSemana: '0',
    cadaHoras: '2',
    horasMultiples: [8, 14, 20],
    minutoMultiples: '0',
    diasSemanaMultiples: [1, 2, 3, 4, 5],
    expresionCronManual: '0 8,14,20 * * *',
    activo: 1
  });
  const [guardandoTarea, setGuardandoTarea] = useState(false);
  const [ejecutandoTareaId, setEjecutandoTareaId] = useState(null);

  // 3. Alineación de Centros de Costo (ERP vs Solicitudes)
  const [modalCCOpen, setModalCCOpen] = useState(false);
  const [idEmpresaCCSelected, setIdEmpresaCCSelected] = useState('');
  const [loadingCC, setLoadingCC] = useState(false);
  const [actualizandoCC, setActualizandoCC] = useState(false);
  const [discrepanciasCC, setDiscrepanciasCC] = useState([]);
  const [selectedCCIds, setSelectedCCIds] = useState(new Set());
  const [busquedaCC, setBusquedaCC] = useState('');

  // 5. Estado de Control Maestro de Solicitudes (Super Admin)
  const [modalControlMaestroOpen, setModalControlMaestroOpen] = useState(false);
  const [catalogosControl, setCatalogosControl] = useState({
    empresas: [],
    compradores: [],
    centrosCosto: [],
    departamentos: []
  });
  const [cargandoCatalogosControl, setCargandoCatalogosControl] = useState(false);
  const [busquedaControl, setBusquedaControl] = useState('');
  const [folioFiltroControl, setFolioFiltroControl] = useState('');
  const [empresaFiltroControl, setEmpresaFiltroControl] = useState('todas');
  const [estatusFiltroControl, setEstatusFiltroControl] = useState('todos');
  const [compradorFiltroControl, setCompradorFiltroControl] = useState('todos');
  const [centroCostoFiltroControl, setCentroCostoFiltroControl] = useState('todos');
  const [departamentoFiltroControl, setDepartamentoFiltroControl] = useState('todos');
  const [resultadosControl, setResultadosControl] = useState([]);
  const [cargandoControl, setCargandoControl] = useState(false);
  const [solicitudSeleccionada, setSolicitudSeleccionada] = useState(null);
  const [cargandoDetalleControl, setCargandoDetalleControl] = useState(false);
  const [tabMaestro, setTabMaestro] = useState(0);
  const [formEdicionMaestra, setFormEdicionMaestra] = useState({
    montoSolicitado: '',
    estatus: 1,
    fechaVencimiento: '',
    proveedor: '',
    centroCostoId: '',
    divisa: 'MXN',
    observaciones: '',
    accionFirmas: 'MANTENER',
    justificacion: ''
  });
  const [guardandoEdicionMaestra, setGuardandoEdicionMaestra] = useState(false);
  const [subiendoArchivoControl, setSubiendoArchivoControl] = useState(false);
  const [reemplazandoArchivoId, setReemplazandoArchivoId] = useState(null);
  const [tipoDocSubir, setTipoDocSubir] = useState('ORDEN_COMPRA');
  const [filtroBitacoraModo, setFiltroBitacoraModo] = useState('ESTA_SOLICITUD');

  // Sincronizar empresa seleccionada para CC
  useEffect(() => {
    if (empresaIds.length === 1) {
      setIdEmpresaCCSelected(empresaIds[0]);
    } else {
      setIdEmpresaCCSelected('ALL');
    }
  }, [empresaIdsKey]);

  // Cargar discrepancias de Centros de Costo
  const handleConsultarDiscrepanciasCC = async () => {
    let targetEmpresas = [];
    if (idEmpresaCCSelected === 'ALL') {
      targetEmpresas = empresaIds.length > 0 ? empresaIds : empresasDisponibles.map(e => Number(e.EMPRESAID));
    } else if (idEmpresaCCSelected) {
      targetEmpresas = [Number(idEmpresaCCSelected)];
    } else if (empresaIds.length > 0) {
      targetEmpresas = empresaIds;
    }

    setLoadingCC(true);
    setSelectedCCIds(new Set());

    try {
      const res = await axios.post(`${apiUrl}/funciones/diferenciasCentrosCosto`, {
        idEmpresa: targetEmpresas
      });

      if (res.data.ok) {
        const rows = res.data.data || [];
        setDiscrepanciasCC(rows);
        if (rows.length === 0) {
          handleOpenSweetAlert('¡Excelente! No se encontraron discrepancias de centros de costo entre el ERP y las solicitudes.', 'success');
        }
      } else {
        handleOpenSweetAlert(res.data.error || 'Error al consultar discrepancias', 'error');
      }
    } catch (err) {
      console.error('Error consultando diferencias CC:', err);
      handleOpenSweetAlert(err.response?.data?.error || 'Error al conectar con el servidor', 'error');
    } finally {
      setLoadingCC(false);
    }
  };

  // Filtrado local de discrepancias
  const discrepanciasFiltradas = useMemo(() => {
    if (!busquedaCC.trim()) return discrepanciasCC;
    const term = busquedaCC.trim().toUpperCase();
    return discrepanciasCC.filter(item =>
      String(item.FOLIO_OC || '').includes(term) ||
      String(item.AUTSOLPAGOID || '').includes(term) ||
      String(item.PROVEEDOR || '').toUpperCase().includes(term) ||
      String(item.NUMERO_CC_ACTUAL || '').toUpperCase().includes(term) ||
      String(item.NOMBRE_CC_ACTUAL || '').toUpperCase().includes(term) ||
      String(item.NUMERO_CC_ERP || '').toUpperCase().includes(term) ||
      String(item.NOMBRE_CC_ERP || '').toUpperCase().includes(term) ||
      String(item.NUMERO_CC_NUEVO || '').toUpperCase().includes(term) ||
      String(item.NOMBRE_CC_NUEVO || '').toUpperCase().includes(term)
    );
  }, [discrepanciasCC, busquedaCC]);

  // Selección de checkboxes
  const handleToggleSelectAllCC = () => {
    const elegibles = discrepanciasFiltradas.filter(item => item.ID_NUEVO != null);
    if (selectedCCIds.size === elegibles.length && elegibles.length > 0) {
      setSelectedCCIds(new Set());
    } else {
      setSelectedCCIds(new Set(elegibles.map(item => item.AUTSOLPAGOID)));
    }
  };

  const handleToggleSelectOneCC = (autsolpagoid) => {
    setSelectedCCIds(prev => {
      const next = new Set(prev);
      if (next.has(autsolpagoid)) {
        next.delete(autsolpagoid);
      } else {
        next.add(autsolpagoid);
      }
      return next;
    });
  };

  // Actualizar solicitudes seleccionadas
  const handleActualizarCCSeleccionados = async () => {
    if (selectedCCIds.size === 0) {
      handleOpenSweetAlert('Selecciona al menos una solicitud para actualizar.', 'warning');
      return;
    }

    const itemsAActualizar = discrepanciasCC.filter(item => selectedCCIds.has(item.AUTSOLPAGOID) && item.ID_NUEVO != null);

    if (itemsAActualizar.length === 0) {
      handleOpenSweetAlert('Ninguna de las solicitudes seleccionadas tiene un CCID válido en el catálogo.', 'warning');
      return;
    }

    setActualizandoCC(true);
    try {
      const res = await axios.post(`${apiUrl}/funciones/actualizarCentrosCostoSolicitudes`, {
        items: itemsAActualizar,
        usuario: loggedInUsername || 'Sistema'
      });

      if (res.data.ok) {
        handleOpenSweetAlert(res.data.mensaje || 'Solicitudes actualizadas con éxito.', 'success');
        // Refrescar listado
        handleConsultarDiscrepanciasCC();
      } else {
        handleOpenSweetAlert(res.data.error || 'Error al actualizar solicitudes.', 'error');
      }
    } catch (err) {
      console.error('Error actualizando solicitudes CC:', err);
      handleOpenSweetAlert(err.response?.data?.error || 'Error al actualizar solicitudes.', 'error');
    } finally {
      setActualizandoCC(false);
    }
  };

  // Cargar empresas permitidas y disponibles
  const cargarEmpresas = useCallback(async () => {
    if (!apiUrl) return;
    try {
      const payloadIds = (empresaIds && empresaIds.length > 0) ? empresaIds : [];
      const res = await axios.post(`${apiUrl}/empresasDisponibles`, { idEmpresa: payloadIds });
      let list = Array.isArray(res.data) ? res.data : [];

      if (list.length === 0) {
        const resTodas = await axios.get(`${apiUrl}/empresasPermitidas`);
        list = Array.isArray(resTodas.data) ? resTodas.data : [];
      }

      setEmpresasDisponibles(list);
    } catch (e) {
      console.error('Error cargando empresas:', e);
      try {
        const resFallback = await axios.get(`${apiUrl}/empresasPermitidas`);
        setEmpresasDisponibles(Array.isArray(resFallback.data) ? resFallback.data : []);
      } catch (err) {}
    }
  }, [apiUrl, empresaIdsKey]);

  useEffect(() => {
    cargarEmpresas();
  }, [cargarEmpresas]);

  // Sincronizar selección de empresa según la sesión activa
  useEffect(() => {
    if (empresaIds.length === 1) {
      setIdEmpresaSelected(empresaIds[0]);
    } else {
      setIdEmpresaSelected('ALL');
    }
  }, [empresaIdsKey]);

  // Ejecutar Auditoría (modo diagnóstico / vista previa)
  const handleEjecutarAuditoria = async () => {
    let targetEmpresas = [];
    if (idEmpresaSelected === 'ALL') {
      targetEmpresas = empresaIds.length > 0 ? empresaIds : empresasDisponibles.map(e => Number(e.EMPRESAID));
    } else if (idEmpresaSelected) {
      targetEmpresas = [Number(idEmpresaSelected)];
    } else if (empresaIds.length > 0) {
      targetEmpresas = empresaIds;
    }

    if (targetEmpresas.length === 0) {
      handleOpenSweetAlert('Selecciona una empresa para auditar.', 'warning');
      return;
    }
    if (modoAuditoria === 'folio' && !folioAuditar.trim()) {
      handleOpenSweetAlert('Ingresa el número de folio de la orden de compra.', 'warning');
      return;
    }
    if (modoAuditoria === 'rango' && (!fechaInicioAuditar || !fechaFinAuditar)) {
      handleOpenSweetAlert('Selecciona las fechas de inicio y fin.', 'warning');
      return;
    }

    setLoadingAuditoria(true);
    setResultadoAuditoria(null);
    setSelectedAuditoriaIds(new Set());

    try {
      const res = await axios.post(`${apiUrl}/auditarOCsCanceladas`, {
        modo: modoAuditoria,
        empresas: targetEmpresas,
        fechaInicio: fechaInicioAuditar,
        fechaFin: fechaFinAuditar,
        folioOC: folioAuditar.trim(),
        marcarCanceladas: false // Solo consulta / diagnóstico preliminar
      });

      if (res.data.ok) {
        setResultadoAuditoria(res.data);
        const detalle = res.data.detalle || [];
        if (detalle.length > 0) {
          // Seleccionar todas por defecto para agilizar
          setSelectedAuditoriaIds(new Set(detalle.map(d => d.IDDATOSERP)));
          handleOpenSweetAlert(
            `Se detectaron ${detalle.length} orden(es) en la BD que ya no existen en el ERP. Selecciona cuáles deseas cancelar.`,
            'warning'
          );
        } else {
          handleOpenSweetAlert('¡Excelente! No se detectaron órdenes canceladas. Todas las OCs están sincronizadas con el ERP.', 'success');
        }
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error al ejecutar auditoría', 'error');
      }
    } catch (err) {
      console.error('Error auditando OCs:', err);
      handleOpenSweetAlert(err.response?.data?.mensaje || 'Error al conectar con la API de auditoría', 'error');
    } finally {
      setLoadingAuditoria(false);
    }
  };

  // Checkboxes de selección para Auditoría
  const handleToggleSelectAllAuditoria = () => {
    const detalle = resultadoAuditoria?.detalle || [];
    if (selectedAuditoriaIds.size === detalle.length && detalle.length > 0) {
      setSelectedAuditoriaIds(new Set());
    } else {
      setSelectedAuditoriaIds(new Set(detalle.map(item => item.IDDATOSERP)));
    }
  };

  const handleToggleSelectOneAuditoria = (idDatosErp) => {
    setSelectedAuditoriaIds(prev => {
      const next = new Set(prev);
      if (next.has(idDatosErp)) {
        next.delete(idDatosErp);
      } else {
        next.add(idDatosErp);
      }
      return next;
    });
  };

  // Aplicar cancelación sobre las OCs seleccionadas
  const handleAplicarCancelacionesSeleccionadas = async () => {
    if (selectedAuditoriaIds.size === 0) {
      handleOpenSweetAlert('Debes seleccionar al menos una orden para cancelar.', 'warning');
      return;
    }

    const detalle = resultadoAuditoria?.detalle || [];
    const itemsACancelar = detalle.filter(item => selectedAuditoriaIds.has(item.IDDATOSERP));

    const confirm = await Swal.fire({
      title: '¿Confirmar Cancelación?',
      html: `Se marcarán como <strong>CANCELADA ERP (1)</strong> en la base de datos <strong>${itemsACancelar.length}</strong> orden(es) de compra seleccionada(s).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d32f2f',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, Cancelar Seleccionadas',
      cancelButtonText: 'Regresar'
    });

    if (!confirm.isConfirmed) return;

    setAplicandoCancelaciones(true);
    try {
      const res = await axios.post(`${apiUrl}/auditarOCsCanceladas/aplicar`, {
        items: itemsACancelar,
        usuario: loggedInUsername || 'Sistema'
      });

      if (res.data.ok) {
        handleOpenSweetAlert(res.data.mensaje || 'Cancelaciones aplicadas correctamente.', 'success');
        const canceladosSet = new Set(itemsACancelar.map(i => i.IDDATOSERP));
        setResultadoAuditoria(prev => {
          if (!prev) return null;
          const nuevoDetalle = (prev.detalle || []).filter(item => !canceladosSet.has(item.IDDATOSERP));
          return {
            ...prev,
            canceladasDetectadas: nuevoDetalle.length,
            canceladasMarcadas: (prev.canceladasMarcadas || 0) + itemsACancelar.length,
            detalle: nuevoDetalle
          };
        });
        setSelectedAuditoriaIds(new Set());
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error al aplicar cancelaciones', 'error');
      }
    } catch (err) {
      console.error('Error aplicando cancelaciones:', err);
      handleOpenSweetAlert(err.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
    } finally {
      setAplicandoCancelaciones(false);
    }
  };

  // Consultar estado de sincronización y auto-alineación
  const handleConsultarEstadoSync = useCallback(async () => {
    setLoadingEstadoSync(true);
    try {
      const res = await axios.get(`${apiUrl}/sistema/estado-sync`);
      if (res.data.ok) {
        setEstadoSync(res.data);
      }
    } catch (e) {
      console.error('Error consultando estado de sync:', e);
    } finally {
      setLoadingEstadoSync(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    handleConsultarEstadoSync();
  }, [handleConsultarEstadoSync]);

  // Consultar estado del Servidor Espejo y Alta Disponibilidad
  const handleConsultarEstadoEspejo = useCallback(async () => {
    setLoadingEspejo(true);
    try {
      const res = await axios.get(`${apiUrl}/sistema/espejo/estado`);
      if (res.data.ok) {
        setEstadoEspejo(res.data);
      }
    } catch (e) {
      console.error('Error consultando estado espejo:', e);
    } finally {
      setLoadingEspejo(false);
    }
  }, [apiUrl]);

  const handleAbrirModalEspejo = () => {
    setModalEspejoOpen(true);
    setResultadoSyncEspejo(null);
    handleConsultarEstadoEspejo();
  };

  // Ejecutar sincronización manual al Espejo con reporte detallado
  const handleEjecutarSincronizacionEspejo = async () => {
    const confirm = await Swal.fire({
      title: '¿Sincronizar Base de Datos Espejo ahora?',
      text: 'Se compararán las 47 tablas de la base principal contra el servidor espejo (10.10.30.5) y se replicarán todos los registros más recientes.',
      icon: 'info',
      showCancelButton: true,
      confirmButtonColor: '#0655A5',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, sincronizar ahora',
      cancelButtonText: 'Cancelar'
    });

    if (!confirm.isConfirmed) return;

    setSincronizandoEspejo(true);
    setResultadoSyncEspejo(null);

    try {
      const res = await axios.post(`${apiUrl}/sistema/espejo/sincronizar`, {
        usuario: loggedInUsername || 'Administrador'
      });

      if (res.data.ok) {
        setResultadoSyncEspejo(res.data);
        handleConsultarEstadoEspejo();
        if (res.data.tablasActualizadas > 0) {
          Swal.fire({
            title: '¡Sincronización Exitosa!',
            html: `Se actualizaron <strong>${res.data.tablasActualizadas} tablas</strong> y <strong>${res.data.totalRegistrosSincronizados?.toLocaleString()} registros</strong> en el servidor espejo.`,
            icon: 'success',
            confirmButtonColor: '#0655A5'
          });
        } else {
          Swal.fire({
            title: '¡Todo al día!',
            text: 'Ambas bases de datos ya estaban 100% sincronizadas. No hubo tablas desfasadas.',
            icon: 'success',
            confirmButtonColor: '#0655A5'
          });
        }
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error al sincronizar espejo', 'error');
      }
    } catch (e) {
      console.error('Error sincronizando espejo:', e);
      handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
    } finally {
      setSincronizandoEspejo(false);
    }
  };

  // Cargar estado de tablas de la BD
  const handleVerTablasBD = async () => {
    setLoadingTablas(true);
    setModalTablasOpen(true);
    try {
      const res = await axios.get(`${apiUrl}/respaldoBD/tablas`);
      if (res.data.ok) {
        setTablasBD(res.data.tablas || []);
      }
    } catch (e) {
      console.error('Error consultando tablas:', e);
      handleOpenSweetAlert('Error al obtener estado de la base de datos', 'error');
    } finally {
      setLoadingTablas(false);
    }
  };

  // Cargar lista de respaldos disponibles
  const handleCargarRespaldos = async () => {
    setLoadingRespaldos(true);
    try {
      const res = await axios.get(`${apiUrl}/sistema/respaldos/lista`);
      if (res.data.ok) {
        setListaRespaldos(res.data.respaldos || []);
      }
    } catch (e) {
      console.error('Error cargando lista de respaldos:', e);
      handleOpenSweetAlert('Error al listar respaldos', 'error');
    } finally {
      setLoadingRespaldos(false);
    }
  };

  // Abrir modal de respaldos
  const handleAbrirModalRespaldos = () => {
    setModalRespaldosOpen(true);
    handleCargarRespaldos();
  };

  // Generar respaldo manual de Base de Datos
  const handleGenerarRespaldoBD = async () => {
    setGenerandoRespaldoDB(true);
    try {
      const res = await axios.post(`${apiUrl}/sistema/respaldo-bd/generar`, {
        usuario: loggedInUsername || 'Sistema'
      });
      if (res.data.ok) {
        handleOpenSweetAlert(`Respaldo de BD generado: ${res.data.resultado?.nombre} (${res.data.resultado?.tamanoMB})`, 'success');
        handleCargarRespaldos();
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error generando respaldo', 'error');
      }
    } catch (e) {
      console.error('Error generando respaldo BD:', e);
      handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
    } finally {
      setGenerandoRespaldoDB(false);
    }
  };

  // Generar respaldo manual de Archivos
  const handleGenerarRespaldoArchivos = async () => {
    setGenerandoRespaldoArchivos(true);
    try {
      const res = await axios.post(`${apiUrl}/sistema/respaldo-archivos/generar`, {
        usuario: loggedInUsername || 'Sistema'
      });
      if (res.data.ok) {
        handleOpenSweetAlert(`Respaldo de archivos generado: ${res.data.resultado?.nombre} (${res.data.resultado?.tamanoMB})`, 'success');
        handleCargarRespaldos();
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error generando respaldo de archivos', 'error');
      }
    } catch (e) {
      console.error('Error generando respaldo de archivos:', e);
      handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
    } finally {
      setGenerandoRespaldoArchivos(false);
    }
  };

  // Descargar archivo de respaldo
  const handleDescargarRespaldo = (respaldo) => {
    const url = `${apiUrl}/sistema/respaldos/descargar/${respaldo.tipo}/${encodeURIComponent(respaldo.nombre)}`;
    window.open(url, '_blank');
  };

  // Eliminar un respaldo específico de forma permanente (Local + Servidor + Bitácora)
  const handleEliminarRespaldo = async (respaldo) => {
    const result = await Swal.fire({
      title: '¿Eliminar este Respaldo?',
      text: `Se borrará el archivo comprimido "${respaldo.nombre}" (${respaldo.tamanoMB}) tanto de tu PC como del Servidor de Red, y se limpiará su registro de bitácora.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar archivo',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const res = await axios.delete(`${apiUrl}/sistema/respaldos/${respaldo.tipo}/${encodeURIComponent(respaldo.nombre)}`);
        if (res.data.ok) {
          handleOpenSweetAlert(`Respaldo "${respaldo.nombre}" eliminado exitosamente.`, 'success');
          handleCargarRespaldos();
        } else {
          handleOpenSweetAlert(res.data.mensaje || 'Error al eliminar respaldo', 'error');
        }
      } catch (e) {
        console.error('Error eliminando respaldo:', e);
        handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
      }
    }
  };

  // Limpiar respaldos antiguos según días seleccionados
  const handleLimpiarRespaldosAntiguos = async () => {
    const result = await Swal.fire({
      title: `¿Depurar respaldos mayores a ${diasRetencion} días?`,
      text: `Se eliminarán los archivos .zip de BD y Archivos con más de ${diasRetencion} días de antigüedad para liberar espacio en disco.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#d97706',
      cancelButtonColor: '#64748b',
      confirmButtonText: `Sí, depurar (> ${diasRetencion} días)`,
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      setLimpiandoAntiguos(true);
      try {
        const res = await axios.post(`${apiUrl}/sistema/respaldos/limpiar-antiguos`, {
          dias: diasRetencion
        });
        if (res.data.ok) {
          handleOpenSweetAlert(res.data.mensaje || 'Limpieza ejecutada con éxito.', 'success');
          handleCargarRespaldos();
        } else {
          handleOpenSweetAlert(res.data.mensaje || 'Error en limpieza', 'error');
        }
      } catch (e) {
        console.error('Error ejecutando limpieza de respaldos:', e);
        handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
      } finally {
        setLimpiandoAntiguos(false);
      }
    }
  };

  // Función para interpretar cualquier expresión Cron en español claro
  const describirExpresionCron = (cronStr) => {
    if (!cronStr || typeof cronStr !== 'string') return 'Ingresa una expresión cron válida de 5 partes.';
    const parts = cronStr.trim().split(/\s+/);
    if (parts.length !== 5) return 'La expresión debe tener exactamente 5 posiciones (ej. Minuto Hora Día Mes DíaSemana).';

    const [min, hor, diaM, mes, diaS] = parts;

    const formatearHora12 = (h, m = '00') => {
      const numH = parseInt(h, 10);
      if (isNaN(numH)) return `${h}:${m}`;
      const ampm = numH >= 12 ? 'PM' : 'AM';
      const h12 = numH % 12 === 0 ? 12 : numH % 12;
      return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')} ${ampm}`;
    };

    const nombresDias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];

    // Caso cada X minutos: */N * * * *
    if (min.startsWith('*/') && hor === '*' && diaM === '*' && mes === '*' && diaS === '*') {
      return `⏱️ Se ejecutará continuamente cada ${min.replace('*/', '')} minutos.`;
    }

    // Caso cada X horas: 0 */N * * *
    if (min === '0' && hor.startsWith('*/') && diaM === '*' && mes === '*' && diaS === '*') {
      return `⏱️ Se ejecutará cada ${hor.replace('*/', '')} horas en punto (:00 min).`;
    }

    // Varios horarios en el día: 0 8,14,20 * * *
    if (diaM === '*' && mes === '*' && diaS === '*') {
      const horas = hor.split(',');
      if (horas.length > 1) {
        const horasTxt = horas.map(h => formatearHora12(h, min)).join(', ');
        return `📅 Se ejecutará todos los días en los siguientes horarios: ${horasTxt}.`;
      }
      if (!isNaN(parseInt(hor, 10)) && !isNaN(parseInt(min, 10))) {
        return `📅 Se ejecutará todos los días a las ${formatearHora12(hor, min)}.`;
      }
    }

    // De Lunes a Viernes: 0 H * * 1-5
    if (diaS === '1-5' && diaM === '*' && mes === '*') {
      return `💼 Se ejecutará de Lunes a Viernes a las ${formatearHora12(hor, min)}.`;
    }

    // Un día específico de la semana: 0 H * * D
    if (!isNaN(parseInt(diaS, 10)) && diaM === '*' && mes === '*') {
      const diaTxt = nombresDias[parseInt(diaS, 10)] || `día ${diaS}`;
      return `📆 Se ejecutará todos los ${diaTxt}s a las ${formatearHora12(hor, min)}.`;
    }

    // Días específicos del mes: 0 H 1,15 * *
    if (diaM !== '*' && mes === '*' && diaS === '*') {
      return `🗓️ Se ejecutará los días ${diaM} de cada mes a las ${formatearHora12(hor, min)}.`;
    }

    return `⚙️ Horario personalizado (${cronStr}) programado.`;
  };

  // =========================================================================
  // GESTOR DE TAREAS Y RUTINAS PROGRAMADAS
  // =========================================================================

  // Cargar lista de tareas
  const handleCargarTareas = async () => {
    setLoadingTareas(true);
    try {
      const res = await axios.get(`${apiUrl}/sistema/tareas`);
      if (res.data.ok) {
        setListaTareas(res.data.tareas || []);
      }
    } catch (e) {
      console.error('Error cargando tareas programadas:', e);
      handleOpenSweetAlert('Error al cargar tareas programadas', 'error');
    } finally {
      setLoadingTareas(false);
    }
  };

  // Abrir modal principal de tareas
  const handleAbrirModalTareas = () => {
    setModalTareasOpen(true);
    handleCargarTareas();
  };

  // Abrir modal para crear nueva tarea
  const handleAbrirCrearTarea = () => {
    setTareaEnEdicion(null);
    setFormTarea({
      nombre: '',
      accion: 'RESPALDO_BD',
      tipoFrecuencia: 'DIARIO',
      horaFija: '03:00',
      diaSemana: '0',
      cadaHoras: '2',
      horasMultiples: [8, 14, 20],
      minutoMultiples: '0',
      diasSemanaMultiples: [1, 2, 3, 4, 5],
      expresionCronManual: '0 8,14,20 * * *',
      activo: 1
    });
    setModalEditarTareaOpen(true);
  };

  // Abrir modal para editar tarea existente
  const handleAbrirEditarTarea = (tarea) => {
    setTareaEnEdicion(tarea);
    let tipoFrecuencia = 'MANUAL';
    let horaFija = '03:00';
    let diaSemana = '0';
    let cadaHoras = '2';
    let horasMultiples = [8, 14, 20];
    let minutoMultiples = '0';
    let diasSemanaMultiples = [1, 2, 3, 4, 5];

    const cronParts = (tarea.EXPRESION_CRON || '').trim().split(/\s+/);
    if (cronParts.length === 5) {
      const [mm, hh, dom, mon, dow] = cronParts;
      if (dom === '*' && mon === '*' && dow === '*' && !isNaN(mm) && !isNaN(hh)) {
        tipoFrecuencia = 'DIARIO';
        horaFija = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
      } else if (dom === '*' && mon === '*' && dow === '*' && hh.includes(',')) {
        tipoFrecuencia = 'VARIOS_HORARIOS';
        horasMultiples = hh.split(',').map(h => parseInt(h, 10)).filter(h => !isNaN(h));
        minutoMultiples = String(mm);
      } else if (dom === '*' && mon === '*' && (dow === '1-5' || dow.includes(','))) {
        tipoFrecuencia = 'DIAS_PERSONALIZADOS';
        if (dow === '1-5') diasSemanaMultiples = [1, 2, 3, 4, 5];
        else diasSemanaMultiples = dow.split(',').map(d => parseInt(d, 10)).filter(d => !isNaN(d));
        horaFija = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
      } else if (dom === '*' && mon === '*' && !isNaN(dow) && !isNaN(mm) && !isNaN(hh)) {
        tipoFrecuencia = 'SEMANAL';
        diaSemana = String(dow);
        horaFija = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
      } else if (mm === '0' && hh.startsWith('*/') && dom === '*' && mon === '*' && dow === '*') {
        tipoFrecuencia = 'CADA_HORAS';
        cadaHoras = hh.replace('*/', '') || '2';
      }
    }

    setFormTarea({
      nombre: tarea.NOMBRE || '',
      accion: tarea.ACCION || 'RESPALDO_BD',
      tipoFrecuencia,
      horaFija,
      diaSemana,
      cadaHoras,
      horasMultiples,
      minutoMultiples,
      diasSemanaMultiples,
      expresionCronManual: tarea.EXPRESION_CRON || '0 3 * * *',
      activo: Number(tarea.ACTIVO) ? 1 : 0
    });
    setModalEditarTareaOpen(true);
  };

  // Guardar (Crear / Editar) tarea
  const handleGuardarTarea = async () => {
    if (!formTarea.nombre.trim()) {
      handleOpenSweetAlert('Por favor ingresa un nombre para la rutina.', 'warning');
      return;
    }

    let expresionCron = '0 3 * * *';
    let frecuenciaTexto = 'Todos los días';

    if (formTarea.tipoFrecuencia === 'DIARIO') {
      const [hh, mm] = (formTarea.horaFija || '03:00').split(':');
      expresionCron = `${parseInt(mm, 10)} ${parseInt(hh, 10)} * * *`;
      frecuenciaTexto = `Todos los días a las ${formTarea.horaFija || '03:00'}`;
    } else if (formTarea.tipoFrecuencia === 'VARIOS_HORARIOS') {
      if (!formTarea.horasMultiples || formTarea.horasMultiples.length === 0) {
        handleOpenSweetAlert('Por favor selecciona al menos una hora en el selector.', 'warning');
        return;
      }
      const horasOrd = [...formTarea.horasMultiples].sort((a, b) => a - b);
      const min = parseInt(formTarea.minutoMultiples || 0, 10);
      expresionCron = `${min} ${horasOrd.join(',')} * * *`;
      const horasFmt = horasOrd.map(h => {
        const ampm = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return `${String(h12).padStart(2, '0')}:${String(min).padStart(2, '0')} ${ampm}`;
      }).join(', ');
      frecuenciaTexto = `Horarios diarios: ${horasFmt}`;
    } else if (formTarea.tipoFrecuencia === 'CADA_HORAS') {
      const n = Math.max(1, parseInt(formTarea.cadaHoras || 2, 10));
      expresionCron = `0 */${n} * * *`;
      frecuenciaTexto = `Cada ${n} hora(s)`;
    } else if (formTarea.tipoFrecuencia === 'DIAS_PERSONALIZADOS') {
      if (!formTarea.diasSemanaMultiples || formTarea.diasSemanaMultiples.length === 0) {
        handleOpenSweetAlert('Por favor selecciona al menos un día de la semana.', 'warning');
        return;
      }
      const [hh, mm] = (formTarea.horaFija || '09:00').split(':');
      const diasNombres = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
      const diasOrd = [...formTarea.diasSemanaMultiples].sort((a, b) => a - b);
      expresionCron = `${parseInt(mm, 10)} ${parseInt(hh, 10)} * * ${diasOrd.join(',')}`;
      const diasTxt = diasOrd.map(d => diasNombres[d]).join(', ');
      frecuenciaTexto = `${diasTxt} a las ${formTarea.horaFija || '09:00'}`;
    } else if (formTarea.tipoFrecuencia === 'SEMANAL') {
      const [hh, mm] = (formTarea.horaFija || '03:00').split(':');
      const dias = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
      const diaNum = parseInt(formTarea.diaSemana || 0, 10);
      expresionCron = `${parseInt(mm, 10)} ${parseInt(hh, 10)} * * ${diaNum}`;
      frecuenciaTexto = `Todos los ${dias[diaNum]} a las ${formTarea.horaFija || '03:00'}`;
    } else {
      expresionCron = formTarea.expresionCronManual.trim();
      frecuenciaTexto = `Cron manual: ${expresionCron}`;
    }

    setGuardandoTarea(true);
    try {
      if (tareaEnEdicion) {
        // Editar
        const res = await axios.put(`${apiUrl}/sistema/tareas/${tareaEnEdicion.IDTAREA}/editar`, {
          nombre: formTarea.nombre,
          accion: formTarea.accion,
          expresionCron,
          frecuenciaTexto,
          activo: formTarea.activo
        });
        if (res.data.ok) {
          handleOpenSweetAlert('Rutina actualizada correctamente.', 'success');
          setModalEditarTareaOpen(false);
          handleCargarTareas();
        } else {
          handleOpenSweetAlert(res.data.mensaje || 'Error al actualizar', 'error');
        }
      } else {
        // Crear
        const res = await axios.post(`${apiUrl}/sistema/tareas/crear`, {
          nombre: formTarea.nombre,
          accion: formTarea.accion,
          expresionCron,
          frecuenciaTexto,
          activo: formTarea.activo,
          usuario: loggedInUsername || 'Sistema'
        });
        if (res.data.ok) {
          handleOpenSweetAlert('Nueva rutina creada y programada con éxito.', 'success');
          setModalEditarTareaOpen(false);
          handleCargarTareas();
        } else {
          handleOpenSweetAlert(res.data.mensaje || 'Error al crear', 'error');
        }
      }
    } catch (e) {
      console.error('Error guardando tarea:', e);
      handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
    } finally {
      setGuardandoTarea(false);
    }
  };

  // Toggle Activo/Inactivo (Switch)
  const handleToggleTarea = async (tarea) => {
    try {
      const res = await axios.put(`${apiUrl}/sistema/tareas/${tarea.IDTAREA}/toggle`);
      if (res.data.ok) {
        setListaTareas(prev => prev.map(t => t.IDTAREA === tarea.IDTAREA ? { ...t, ACTIVO: res.data.activo } : t));
      }
    } catch (e) {
      console.error('Error cambiando estado de tarea:', e);
      handleOpenSweetAlert('Error al cambiar estado de la rutina', 'error');
    }
  };

  // Eliminar tarea
  const handleEliminarTarea = async (tarea) => {
    const result = await Swal.fire({
      title: '¿Eliminar Rutina Programada?',
      text: `Se eliminará la rutina "${tarea.NOMBRE}". Ya no se ejecutará automáticamente.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const res = await axios.delete(`${apiUrl}/sistema/tareas/${tarea.IDTAREA}`);
        if (res.data.ok) {
          handleOpenSweetAlert('Rutina eliminada correctamente.', 'success');
          handleCargarTareas();
        }
      } catch (e) {
        console.error('Error eliminando tarea:', e);
        handleOpenSweetAlert('Error al eliminar la rutina', 'error');
      }
    }
  };

  // Ejecutar tarea manualmente a demanda
  const handleEjecutarTareaManual = async (tarea) => {
    setEjecutandoTareaId(tarea.IDTAREA);
    try {
      const res = await axios.post(`${apiUrl}/sistema/tareas/${tarea.IDTAREA}/ejecutar`, {
        usuario: loggedInUsername || 'Usuario'
      });
      if (res.data.ok) {
        handleOpenSweetAlert(res.data.mensaje || 'Rutina ejecutada correctamente.', 'success');
        handleCargarTareas();
        handleConsultarEstadoSync();
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error al ejecutar', 'error');
      }
    } catch (e) {
      console.error('Error ejecutando tarea:', e);
      handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al ejecutar rutina', 'error');
    } finally {
      setEjecutandoTareaId(null);
    }
  };

  // Ejecutar Auto-Mantenimiento a demanda
  const handleEjecutarAutoMantenimiento = async () => {
    setEjecutandoAutoMant(true);
    try {
      const res = await axios.post(`${apiUrl}/sistema/auto-mantenimiento`, {
        usuario: loggedInUsername || 'Sistema'
      });
      if (res.data.ok) {
        handleOpenSweetAlert(res.data.mensaje || 'Mantenimiento ejecutado exitosamente.', 'success');
        handleConsultarEstadoSync();
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error en mantenimiento', 'error');
      }
    } catch (e) {
      console.error('Error ejecutando auto-mantenimiento:', e);
      handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
    } finally {
      setEjecutandoAutoMant(false);
    }
  };

  // ── HANDLERS CONTROL MAESTRO DE SOLICITUDES ──
  const cargarCatalogosControl = useCallback(async () => {
    if (!apiUrl) return;
    setCargandoCatalogosControl(true);
    try {
      const res = await axios.get(`${apiUrl}/sistema/control-solicitud/catalogos`);
      if (res.data.ok) {
        setCatalogosControl({
          empresas: res.data.empresas || [],
          compradores: res.data.compradores || [],
          centrosCosto: res.data.centrosCosto || [],
          departamentos: res.data.departamentos || []
        });
      }
    } catch (e) {
      console.error('Error cargando catálogos de Control Maestro:', e);
    } finally {
      setCargandoCatalogosControl(false);
    }
  }, [apiUrl]);

  const handleAbrirControlMaestro = () => {
    setModalControlMaestroOpen(true);
    cargarCatalogosControl();
    // Ejecutar búsqueda inicial si no hay resultados
    if (resultadosControl.length === 0) {
      handleBuscarControlMaestro();
    }
  };

  const handleLimpiarFiltrosControl = () => {
    setBusquedaControl('');
    setFolioFiltroControl('');
    setEmpresaFiltroControl('todas');
    setEstatusFiltroControl('todos');
    setCompradorFiltroControl('todos');
    setCentroCostoFiltroControl('todos');
    setDepartamentoFiltroControl('todos');
  };

  const handleBuscarControlMaestro = async () => {
    setCargandoControl(true);
    try {
      const res = await axios.get(`${apiUrl}/sistema/control-solicitud/buscar`, {
        params: {
          busqueda: busquedaControl,
          folio: folioFiltroControl,
          empresaId: empresaFiltroControl,
          estatus: estatusFiltroControl,
          comprador: compradorFiltroControl,
          centroCosto: centroCostoFiltroControl,
          departamento: departamentoFiltroControl,
          limit: 50
        }
      });
      if (res.data.ok) {
        setResultadosControl(res.data.solicitudes || []);
        if ((res.data.solicitudes || []).length === 0) {
          handleOpenSweetAlert('No se encontraron solicitudes con los filtros aplicados.', 'info');
        }
      }
    } catch (e) {
      console.error('Error buscando solicitudes:', e);
      handleOpenSweetAlert('Error al buscar solicitudes en el servidor', 'error');
    } finally {
      setCargandoControl(false);
    }
  };

  const handleSeleccionarSolicitudControl = async (idFolio) => {
    setCargandoDetalleControl(true);
    try {
      const res = await axios.get(`${apiUrl}/sistema/control-solicitud/detalle/${idFolio}`);
      if (res.data.ok) {
        const data = res.data;
        setSolicitudSeleccionada(data);
        const sol = data.solicitud;
        setFormEdicionMaestra({
          montoSolicitado: sol.MONTO_SOLICITADO || '',
          estatus: Number(sol.ESTATUS || 1),
          fechaVencimiento: sol.FECHA_VENCIMIENTO ? new Date(sol.FECHA_VENCIMIENTO).toISOString().slice(0, 10) : '',
          proveedor: sol.PROVEEDOR || '',
          centroCostoId: sol.CENTROCOSTOID || '',
          divisa: sol.DIVISA || 'MXN',
          observaciones: sol.OBSERVACIONES || '',
          accionFirmas: 'MANTENER',
          justificacion: ''
        });
        setTabMaestro(0);
      }
    } catch (e) {
      console.error('Error obteniendo detalle de solicitud:', e);
      handleOpenSweetAlert('Error al cargar la información de la solicitud', 'error');
    } finally {
      setCargandoDetalleControl(false);
    }
  };

  const handleGuardarEdicionMaestra = async () => {
    if (!solicitudSeleccionada?.solicitud?.AUTSOLPAGOID) return;
    if (!formEdicionMaestra.justificacion.trim()) {
      handleOpenSweetAlert('Por favor ingresa una justificación o motivo del cambio (requerido para la bitácora).', 'warning');
      return;
    }

    const sol = solicitudSeleccionada.solicitud;
    const montoPrev = Number(sol.MONTO_SOLICITADO || 0);
    const montoNuevo = Number(formEdicionMaestra.montoSolicitado || 0);
    const estatusPrev = Number(sol.ESTATUS || 1);
    const estatusNuevo = Number(formEdicionMaestra.estatus);

    const result = await Swal.fire({
      title: '¿Confirmar Modificación Maestra?',
      html: `
        <div style="text-align:left; font-size:13px; color:#334155;">
          <p>Se aplicarán los cambios a la Solicitud <strong>#${sol.AUTSOLPAGOID}</strong> (OC: <strong>${sol.FOLIO_OC}</strong>):</p>
          <ul>
            <li><strong>Monto:</strong> $${montoPrev.toLocaleString()} ➡️ <strong>$${montoNuevo.toLocaleString()} ${formEdicionMaestra.divisa}</strong></li>
            <li><strong>Estatus:</strong> ${estatusPrev} ➡️ <strong>${estatusNuevo}</strong></li>
            <li><strong>Firmas:</strong> ${formEdicionMaestra.accionFirmas}</li>
            <li><strong>Recálculo en tblpagosacum:</strong> Automático en tiempo real</li>
          </ul>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#0655A5',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, aplicar cambios',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setGuardandoEdicionMaestra(true);
    try {
      const res = await axios.post(`${apiUrl}/sistema/control-solicitud/guardar`, {
        idFolio: sol.AUTSOLPAGOID,
        montoSolicitado: montoNuevo,
        estatus: estatusNuevo,
        fechaVencimiento: formEdicionMaestra.fechaVencimiento,
        proveedor: formEdicionMaestra.proveedor,
        centroCostoId: formEdicionMaestra.centroCostoId,
        divisa: formEdicionMaestra.divisa,
        observaciones: formEdicionMaestra.observaciones,
        accionFirmas: formEdicionMaestra.accionFirmas,
        usuario: loggedInUsername || 'Administrador',
        justificacion: formEdicionMaestra.justificacion
      });

      if (res.data.ok) {
        handleOpenSweetAlert(`Solicitud #${sol.AUTSOLPAGOID} actualizada con éxito.`, 'success');
        handleSeleccionarSolicitudControl(sol.AUTSOLPAGOID);
        handleBuscarControlMaestro();
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error al guardar', 'error');
      }
    } catch (e) {
      console.error('Error guardando edición maestra:', e);
      handleOpenSweetAlert(e.response?.data?.mensaje || 'Error al conectar con el servidor', 'error');
    } finally {
      setGuardandoEdicionMaestra(false);
    }
  };

  const handleSubirArchivoControl = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !solicitudSeleccionada?.solicitud?.AUTSOLPAGOID) return;

    const sol = solicitudSeleccionada.solicitud;
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('idFolio', sol.AUTSOLPAGOID);
    formData.append('tipoDocumento', tipoDocSubir);
    formData.append('monto', sol.MONTO_SOLICITADO);
    formData.append('usuario', loggedInUsername || 'Admin');

    setSubiendoArchivoControl(true);
    try {
      const res = await axios.post(`${apiUrl}/sistema/control-solicitud/archivos/subir`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.ok) {
        handleOpenSweetAlert('Documento subido y asociado correctamente.', 'success');
        handleSeleccionarSolicitudControl(sol.AUTSOLPAGOID);
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error al subir', 'error');
      }
    } catch (e) {
      console.error('Error subiendo archivo:', e);
      handleOpenSweetAlert('Error al subir archivo al servidor', 'error');
    } finally {
      setSubiendoArchivoControl(false);
      e.target.value = '';
    }
  };

  const handleEliminarArchivoControl = async (tabla, idDoc) => {
    const result = await Swal.fire({
      title: '¿Eliminar Documento?',
      text: 'Se eliminará la asociación de este documento con la solicitud.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        const res = await axios.delete(`${apiUrl}/sistema/control-solicitud/archivos/${tabla}/${idDoc}`, {
          data: { usuario: loggedInUsername || 'Admin' }
        });
        if (res.data.ok) {
          handleOpenSweetAlert('Documento eliminado correctamente.', 'success');
          if (solicitudSeleccionada?.solicitud?.AUTSOLPAGOID) {
            handleSeleccionarSolicitudControl(solicitudSeleccionada.solicitud.AUTSOLPAGOID);
          }
        }
      } catch (e) {
        console.error('Error eliminando archivo:', e);
        handleOpenSweetAlert('Error al eliminar archivo', 'error');
      }
    }
  };
  const handleReemplazarArchivoControl = async (e, tabla, idDoc) => {
    const file = e.target.files?.[0];
    if (!file || !solicitudSeleccionada?.solicitud?.AUTSOLPAGOID) return;

    const sol = solicitudSeleccionada.solicitud;
    const formData = new FormData();
    formData.append('archivo', file);
    formData.append('tabla', tabla);
    formData.append('id', idDoc);
    formData.append('usuario', loggedInUsername || 'Admin');

    setReemplazandoArchivoId(idDoc);
    try {
      const res = await axios.post(`${apiUrl}/sistema/control-solicitud/archivos/reemplazar`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.ok) {
        handleOpenSweetAlert(res.data.mensaje || 'Archivo reemplazado correctamente.', 'success');
        handleSeleccionarSolicitudControl(sol.AUTSOLPAGOID);
      } else {
        handleOpenSweetAlert(res.data.mensaje || 'Error al reemplazar', 'error');
      }
    } catch (err) {
      console.error('Error reemplazando archivo:', err);
      handleOpenSweetAlert('Error al reemplazar archivo en el servidor', 'error');
    } finally {
      setReemplazandoArchivoId(null);
      e.target.value = '';
    }
  };

  return (
    <Box sx={{ p: 'clamp(8px, 1.2vw, 16px)', bgcolor: '#f8fafc', minHeight: '100%', overflowY: 'auto' }}>
      {/* ── ENCABEZADO PREMIUM COMPACTO ── */}
      <Paper
        elevation={0}
        sx={{
          p: 'clamp(8px, 1.2vw, 14px)',
          mb: 'clamp(8px, 1.2vw, 14px)',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 'clamp(28px, 2.5vw, 34px)',
              height: 'clamp(28px, 2.5vw, 34px)',
              borderRadius: '8px',
              bgcolor: 'rgba(6, 85, 165, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#0655A5'
            }}
          >
            <BuildCircleIcon sx={{ fontSize: 'clamp(16px, 1.4vw, 20px)' }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                color: '#0f172a',
                fontSize: 'clamp(0.85rem, 0.82rem + 0.25vw, 1.10rem)',
                lineHeight: 1.2,
                letterSpacing: '-0.01em'
              }}
            >
              Herramientas y Funciones del Sistema
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                mt: 0.2,
                fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)'
              }}
            >
              Auditoría de órdenes canceladas en ERP, mantenimiento y monitoreo de base de datos.
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            size="small"
            variant="contained"
            onClick={handleAbrirControlMaestro}
            startIcon={<AdminPanelSettingsIcon sx={{ fontSize: 'clamp(14px, 1.2vw, 17px)' }} />}
            sx={{
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              color: '#ffffff',
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: '6px',
              fontSize: 'clamp(0.60rem, 0.58rem + 0.06vw, 0.70rem)',
              boxShadow: '0 2px 6px rgba(6, 85, 165, 0.3)',
              px: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #05488c 0%, #0655A5 100%)'
              }
            }}
          >
            Control Maestro de Solicitudes
          </Button>

          <Chip
            label={nombreEmpresa || 'TODAS'}
            size="small"
            sx={{
              ...getEmpresaChipSx(nombreEmpresa),
              height: 'clamp(18px, 1.6vw, 22px)',
              fontWeight: 800
            }}
          />
        </Box>
      </Paper>

      {/* ── GRID AUTO-RESPONSIVO CON CSS GRID (REPEAT AUTO-FIT) ── */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, clamp(280px, 24vw, 380px)), 1fr))',
          gap: 'clamp(10px, 1.2vw, 16px)',
          alignItems: 'stretch'
        }}
      >
        {/* CARD 0: CONTROL MAESTRO Y ADMINISTRACIÓN TOTAL DE SOLICITUDES */}
        <Card
          elevation={0}
          sx={{
            borderRadius: '10px',
            border: '2px solid #0655A5',
            bgcolor: '#ffffff',
            boxShadow: '0 2px 8px rgba(6, 85, 165, 0.08)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 18px rgba(6, 85, 165, 0.15)'
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1, p: 'clamp(10px, 1.2vw, 16px)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 'clamp(28px, 2.5vw, 34px)',
                  height: 'clamp(28px, 2.5vw, 34px)',
                  borderRadius: '7px',
                  bgcolor: 'rgba(6, 85, 165, 0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0655A5'
                }}
              >
                <AdminPanelSettingsIcon sx={{ fontSize: 'clamp(16px, 1.4vw, 20px)' }} />
              </Box>
              <Chip
                label="Supervisión Total"
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: '#dbeafe',
                  color: '#1e40af',
                  fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)',
                  height: '18px'
                }}
              />
            </Stack>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 0.5,
                fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)'
              }}
            >
              Control Maestro de Solicitudes
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                lineHeight: 1.35,
                fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)'
              }}
            >
              Edita montos con recálculo automático de saldos en <code>tblpagosacum</code>, cambia estatus, fechas, autorizaciones y gestiona comprobantes y facturas.
            </Typography>
          </CardContent>

          <Divider />
          <CardActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAbrirControlMaestro}
              startIcon={<TuneIcon sx={{ fontSize: 15 }} />}
              sx={{
                background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                fontWeight: 800,
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)',
                height: 'clamp(26px, 2.2vw, 32px)',
                boxShadow: '0 1px 4px rgba(6, 85, 165, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #05488c 0%, #0655A5 100%)'
                }
              }}
            >
              Abrir Control Maestro
            </Button>
          </CardActions>
        </Card>
        {/* CARD 1: AUDITORÍA Y CANCELACIÓN AUTOMÁTICA */}
        <Card
          elevation={0}
          sx={{
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 14px rgba(6, 85, 165, 0.08)',
              borderColor: '#cbd5e1'
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1, p: 'clamp(10px, 1.2vw, 16px)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 'clamp(28px, 2.5vw, 34px)',
                  height: 'clamp(28px, 2.5vw, 34px)',
                  borderRadius: '7px',
                  bgcolor: 'rgba(239, 68, 68, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#dc2626'
                }}
              >
                <FindReplaceIcon sx={{ fontSize: 'clamp(15px, 1.3vw, 19px)' }} />
              </Box>
              <Chip
                label="Auditoría ERP"
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: '#fee2e2',
                  color: '#991b1b',
                  fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)',
                  height: '18px'
                }}
              />
            </Stack>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 0.5,
                fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)'
              }}
            >
              Auditoría de OCs Canceladas
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                lineHeight: 1.35,
                fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)'
              }}
            >
              Compara las órdenes en base de datos contra el ERP. Si una OC fue borrada o cancelada en ERP, la actualiza a <strong>CANCELADO = 1</strong>.
            </Typography>
          </CardContent>

          <Divider />
          <CardActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setModalAuditoriaOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                fontWeight: 800,
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)',
                height: 'clamp(26px, 2.2vw, 32px)',
                boxShadow: '0 1px 4px rgba(6, 85, 165, 0.2)'
              }}
            >
              Abrir Auditoría
            </Button>
          </CardActions>
        </Card>

        {/* CARD 2: ESTADO Y MONITOREO DE BASE DE DATOS */}
        <Card
          elevation={0}
          sx={{
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 14px rgba(6, 85, 165, 0.08)',
              borderColor: '#cbd5e1'
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1, p: 'clamp(10px, 1.2vw, 16px)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 'clamp(28px, 2.5vw, 34px)',
                  height: 'clamp(28px, 2.5vw, 34px)',
                  borderRadius: '7px',
                  bgcolor: 'rgba(6, 85, 165, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0655A5'
                }}
              >
                <StorageIcon sx={{ fontSize: 'clamp(15px, 1.3vw, 19px)' }} />
              </Box>
              <Chip
                label="Estructura BD"
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: '#e0f2fe',
                  color: '#0369a1',
                  fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)',
                  height: '18px'
                }}
              />
            </Stack>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 0.5,
                fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)'
              }}
            >
              Monitoreo y Tablas BD
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                lineHeight: 1.35,
                fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)'
              }}
            >
              Inspecciona el tamaño en disco de las tablas principales, conteo de filas y estado de salud de MySQL.
            </Typography>
          </CardContent>

          <Divider />
          <CardActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc', display: 'flex', gap: 1 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleVerTablasBD}
              sx={{
                borderColor: '#0655A5',
                color: '#0655A5',
                fontWeight: 800,
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
                height: 'clamp(26px, 2.2vw, 32px)',
                '&:hover': { bgcolor: 'rgba(6, 85, 165, 0.04)', borderColor: '#0655A5' }
              }}
            >
              Ver Tablas BD
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAbrirModalRespaldos}
              startIcon={<CloudDownloadIcon sx={{ fontSize: 15 }} />}
              sx={{
                background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                color: '#ffffff',
                fontWeight: 800,
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
                height: 'clamp(26px, 2.2vw, 32px)',
                boxShadow: 'none'
              }}
            >
              Respaldos
            </Button>
          </CardActions>
        </Card>

        {/* CARD 3: ALINEACIÓN DE CENTROS DE COSTO (ERP vs SOLICITUDES) */}
        <Card
          elevation={0}
          sx={{
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 14px rgba(2, 132, 199, 0.08)',
              borderColor: '#cbd5e1'
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1, p: 'clamp(10px, 1.2vw, 16px)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 'clamp(28px, 2.5vw, 34px)',
                  height: 'clamp(28px, 2.5vw, 34px)',
                  borderRadius: '7px',
                  bgcolor: 'rgba(2, 132, 199, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#0284c7'
                }}
              >
                <SyncAltIcon sx={{ fontSize: 'clamp(15px, 1.3vw, 19px)' }} />
              </Box>
              <Chip
                label="Sincronización CC"
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: '#e0f2fe',
                  color: '#0369a1',
                  fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)',
                  height: '18px'
                }}
              />
            </Stack>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 0.5,
                fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)'
              }}
            >
              Alineación de Centros de Costo
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                lineHeight: 1.35,
                fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)'
              }}
            >
              Compara el Centro de Costo asignado en las solicitudes contra el del ERP y actualiza selectivamente registrando en bitácora.
            </Typography>
          </CardContent>

          <Divider />
          <CardActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                setModalCCOpen(true);
                handleConsultarDiscrepanciasCC();
              }}
              sx={{
                background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                fontWeight: 800,
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)',
                height: 'clamp(26px, 2.2vw, 32px)',
                boxShadow: '0 1px 4px rgba(2, 132, 199, 0.2)'
              }}
            >
              Alinear Centros de Costo
            </Button>
          </CardActions>
        </Card>

        {/* CARD 4: BASE DE DATOS ESPEJO Y FAILOVER */}
        <Card
          elevation={0}
          sx={{
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            bgcolor: '#ffffff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            transition: 'transform 0.15s, box-shadow 0.15s, border-color 0.15s',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 14px rgba(16, 185, 129, 0.12)',
              borderColor: '#10b981'
            }
          }}
        >
          <CardContent sx={{ flexGrow: 1, p: 'clamp(10px, 1.2vw, 16px)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Box
                sx={{
                  width: 'clamp(28px, 2.5vw, 34px)',
                  height: 'clamp(28px, 2.5vw, 34px)',
                  borderRadius: '7px',
                  bgcolor: 'rgba(16, 185, 129, 0.10)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#059669'
                }}
              >
                <DnsIcon sx={{ fontSize: 'clamp(15px, 1.3vw, 19px)' }} />
              </Box>
              <Chip
                label={estadoEspejo?.mirrorConectado ? 'Espejo Conectado' : 'Alta Disponibilidad'}
                size="small"
                sx={{
                  fontWeight: 800,
                  bgcolor: estadoEspejo?.mirrorConectado ? '#d1fae5' : '#f1f5f9',
                  color: estadoEspejo?.mirrorConectado ? '#065f46' : '#475569',
                  fontSize: 'clamp(0.58rem, 0.55rem + 0.05vw, 0.66rem)',
                  height: '18px'
                }}
              />
            </Stack>

            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 800,
                color: '#0f172a',
                mb: 0.5,
                fontSize: 'clamp(0.74rem, 0.70rem + 0.08vw, 0.84rem)'
              }}
            >
              Base de Datos Espejo (Failover)
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                lineHeight: 1.35,
                fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)'
              }}
            >
              Monitorea el servidor espejo (10.10.30.5), verifica la paridad de tablas y sincroniza en tiempo real con reporte detallado.
            </Typography>
          </CardContent>

          <Divider />
          <CardActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleAbrirModalEspejo}
              startIcon={<AltRouteIcon sx={{ fontSize: 15 }} />}
              sx={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                fontWeight: 800,
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)',
                height: 'clamp(26px, 2.2vw, 32px)',
                boxShadow: '0 1px 4px rgba(16, 185, 129, 0.25)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #047857 0%, #059669 100%)'
                }
              }}
            >
              Sincronizar Espejo
            </Button>
          </CardActions>
        </Card>
      </Box>

      {/* ── BARRA INFORMATIVA INFERIOR: ESTADO DEL SISTEMA Y ÚLTIMA SINCRONIZACIÓN ── */}
      <Paper
        elevation={0}
        sx={{
          p: 'clamp(10px, 1.2vw, 16px)',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
          bgcolor: '#ffffff',
          boxShadow: '0 1px 4px rgba(0,0,0,0.02)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '8px',
              bgcolor: '#f0fdf4',
              border: '1px solid #bbf7d0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#16a34a'
            }}
          >
            <ScheduleIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <Typography sx={{ fontSize: 'clamp(0.70rem, 0.68rem + 0.08vw, 0.80rem)', fontWeight: 800, color: '#0f172a' }}>
                Última Sincronización con ERP:
              </Typography>
              <Chip
                label={
                  estadoSync?.ultimaSync?.fecha
                    ? `${estadoSync.ultimaSync.fecha} ${estadoSync.ultimaSync.hora || ''}`
                    : 'Sin registros recientes'
                }
                size="small"
                sx={{
                  fontWeight: 800,
                  fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)',
                  bgcolor: '#ecfdf5',
                  color: '#065f46',
                  border: '1px solid #a7f3d0',
                  height: 20
                }}
              />
              {estadoSync?.ultimaSync?.usuario && (
                <Typography sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#64748b', fontWeight: 600 }}>
                  por <strong>{estadoSync.ultimaSync.usuario}</strong>
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#64748b', mt: 0.2 }}>
              ⚙️ Auto-mantenimiento activo: Centros de Costo alineados en cada sync y tarea nocturna diaria a las 03:00 AM (CDMX).
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Tooltip title="Actualizar información de estado del servidor" arrow>
            <Button
              size="small"
              variant="outlined"
              onClick={handleConsultarEstadoSync}
              disabled={loadingEstadoSync}
              startIcon={<RefreshIcon sx={{ fontSize: 15 }} />}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
                height: 28,
                color: '#64748b',
                borderColor: '#cbd5e1'
              }}
            >
              Refrescar
            </Button>
          </Tooltip>

          <Button
            size="small"
            variant="contained"
            onClick={handleAbrirModalTareas}
            startIcon={<AlarmOnIcon sx={{ fontSize: 16 }} />}
            sx={{
              background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
              color: '#ffffff',
              fontWeight: 800,
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
              height: 28,
              boxShadow: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #4338ca 0%, #3730a3 100%)' }
            }}
          >
            Programador de Rutinas
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleEjecutarAutoMantenimiento}
            disabled={ejecutandoAutoMant}
            startIcon={ejecutandoAutoMant ? <CircularProgress size={13} color="inherit" /> : <AutoFixHighIcon sx={{ fontSize: 15 }} />}
            sx={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: '#ffffff',
              fontWeight: 800,
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
              height: 28,
              boxShadow: 'none',
              '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }
            }}
          >
            {ejecutandoAutoMant ? 'Ejecutando...' : 'Ejecutar Auto-Mantenimiento Ahora'}
          </Button>
        </Box>
      </Paper>

      {/* MODAL AUDITORÍA DE CANCELADAS */}
      <Dialog
        open={modalAuditoriaOpen}
        onClose={() => !loadingAuditoria && setModalAuditoriaOpen(false)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            px: 'clamp(12px, 1.5vw, 18px)',
            py: 'clamp(8px, 1vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 'clamp(0.82rem, 0.78rem + 0.18vw, 0.98rem)' }}>
              Auditoría y Cancelación de Órdenes ERP
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)' }}>
              Compara OCs activas en la BD vs las existentes en el ERP para detectar y marcar canceladas
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setModalAuditoriaOpen(false)} sx={{ color: '#ffffff', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 'clamp(12px, 1.5vw, 18px)', bgcolor: '#ffffff' }}>
          <Stack spacing={1.5} sx={{ mt: 0.8 }}>
            <Grid container spacing={1.2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={elegantInputSx}>
                  <InputLabel>Empresa a Auditar</InputLabel>
                  <Select
                    value={idEmpresaSelected}
                    label="Empresa a Auditar"
                    onChange={(e) => setIdEmpresaSelected(e.target.value)}
                    MenuProps={{ disableRestoreFocus: true }}
                  >
                    <MenuItem value="ALL">
                      <em>-- Todas las Empresas Asignadas --</em>
                    </MenuItem>
                    {empresasDisponibles.map((emp) => (
                      <MenuItem key={emp.EMPRESAID} value={emp.EMPRESAID}>
                        {emp.NOMBRE_EMPRESA}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" sx={elegantInputSx}>
                  <InputLabel>Modo de Búsqueda</InputLabel>
                  <Select
                    value={modoAuditoria}
                    label="Modo de Búsqueda"
                    onChange={(e) => setModoAuditoria(e.target.value)}
                    MenuProps={{ disableRestoreFocus: true }}
                  >
                    <MenuItem value="rango">Por Rango de Fechas de OC</MenuItem>
                    <MenuItem value="folio">Por Folio Específico (Ej: 46166)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {modoAuditoria === 'folio' ? (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Folio de Orden de Compra"
                    value={folioAuditar}
                    onChange={(e) => setFolioAuditar(e.target.value)}
                    placeholder="Ejemplo: 46166"
                    sx={elegantInputSx}
                  />
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Fecha Inicio"
                      InputLabelProps={{ shrink: true }}
                      value={fechaInicioAuditar}
                      onChange={(e) => setFechaInicioAuditar(e.target.value)}
                      sx={elegantInputSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Fecha Fin"
                      InputLabelProps={{ shrink: true }}
                      value={fechaFinAuditar}
                      onChange={(e) => setFechaFinAuditar(e.target.value)}
                      sx={elegantInputSx}
                    />
                  </Grid>
                </>
              )}
            </Grid>

            {loadingAuditoria && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2.5, gap: 0.8 }}>
                <CircularProgress size={26} sx={{ color: '#0655A5' }} />
                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600, fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)' }}>
                  Consultando ERP y comparando registros...
                </Typography>
              </Box>
            )}

            {resultadoAuditoria && !loadingAuditoria && (
              <Box sx={{ mt: 0.8 }}>
                <Alert
                  severity={resultadoAuditoria.canceladasDetectadas > 0 ? 'warning' : 'success'}
                  sx={{
                    mb: 1.2,
                    borderRadius: '7px',
                    fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                    py: 0.4,
                    '& .MuiAlert-icon': { fontSize: '1.0rem' }
                  }}
                >
                  {resultadoAuditoria.canceladasDetectadas > 0 ? (
                    <span>
                      Se detectaron <strong>{resultadoAuditoria.canceladasDetectadas}</strong> órdenes en la BD que ya no existen en el ERP. Selecciona las que deseas marcar como canceladas:
                    </span>
                  ) : (
                    <span>
                      Todas las órdenes consultadas (Total: {resultadoAuditoria.totalEnBD}) siguen vigentes en el ERP. No hay cancelaciones pendientes.
                    </span>
                  )}
                </Alert>

                {resultadoAuditoria.detalle && resultadoAuditoria.detalle.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)', fontWeight: 700, color: '#334155' }}>
                        {selectedAuditoriaIds.size} de {resultadoAuditoria.detalle.length} orden(es) seleccionada(s)
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleToggleSelectAllAuditoria}
                        sx={{
                          fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
                          textTransform: 'none',
                          fontWeight: 700,
                          py: 0.2
                        }}
                      >
                        {selectedAuditoriaIds.size === resultadoAuditoria.detalle.length ? 'Deseleccionar Todas' : 'Seleccionar Todas'}
                      </Button>
                    </Box>

                    <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: '7px', overflow: 'hidden', maxHeight: 260, overflowY: 'auto' }}>
                      <Table size="small" stickyHeader>
                        <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                          <TableRow>
                            <TableCell padding="checkbox" sx={{ py: 0.4, bgcolor: '#f1f5f9' }}>
                              <Checkbox
                                size="small"
                                checked={resultadoAuditoria.detalle.length > 0 && selectedAuditoriaIds.size === resultadoAuditoria.detalle.length}
                                indeterminate={selectedAuditoriaIds.size > 0 && selectedAuditoriaIds.size < resultadoAuditoria.detalle.length}
                                onChange={handleToggleSelectAllAuditoria}
                                sx={{ p: 0.5, color: '#0655A5', '&.Mui-checked': { color: '#0655A5' } }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Folio OC</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Empresa</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Fecha OC</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Proveedor</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Total</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Nuevo Estado</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {resultadoAuditoria.detalle.map((row) => {
                            const isSelected = selectedAuditoriaIds.has(row.IDDATOSERP);
                            return (
                              <TableRow
                                key={row.IDDATOSERP}
                                hover
                                onClick={() => handleToggleSelectOneAuditoria(row.IDDATOSERP)}
                                sx={{ cursor: 'pointer', bgcolor: isSelected ? 'rgba(6, 85, 165, 0.04)' : 'inherit' }}
                              >
                                <TableCell padding="checkbox" sx={{ py: 0.4 }}>
                                  <Checkbox
                                    size="small"
                                    checked={isSelected}
                                    onChange={() => handleToggleSelectOneAuditoria(row.IDDATOSERP)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{ p: 0.5, color: '#0655A5', '&.Mui-checked': { color: '#0655A5' } }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontWeight: 800, color: '#0655A5', fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.5 }}>
                                  {row.FOLIO_OC}
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Chip label={row.EMPRESA} size="small" sx={{ ...getEmpresaChipSx(row.EMPRESA), height: 18 }} />
                                </TableCell>
                                <TableCell sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#64748b', py: 0.5 }}>
                                  {row.FECHA_OC}
                                </TableCell>
                                <TableCell sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#334155', py: 0.5, maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {row.PROVEEDOR}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 700, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.5 }}>
                                  ${Number(row.TOTAL || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                                </TableCell>
                                <TableCell sx={{ py: 0.5 }}>
                                  <Chip
                                    label="Cancelada ERP (1)"
                                    size="small"
                                    sx={{
                                      bgcolor: '#fee2e2',
                                      color: '#991b1b',
                                      fontWeight: 800,
                                      fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.65rem)',
                                      height: 18
                                    }}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            onClick={() => setModalAuditoriaOpen(false)}
            disabled={loadingAuditoria || aplicandoCancelaciones}
            sx={{
              textTransform: 'none',
              color: '#64748b',
              fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)'
            }}
          >
            Cerrar
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleEjecutarAuditoria}
              disabled={loadingAuditoria || aplicandoCancelaciones}
              startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
              sx={{
                borderColor: '#0655A5',
                color: '#0655A5',
                fontWeight: 700,
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                height: 'clamp(26px, 2.2vw, 30px)',
                px: 1.5,
                '&:hover': { bgcolor: 'rgba(6, 85, 165, 0.04)', borderColor: '#0861B8' }
              }}
            >
              {loadingAuditoria ? 'Buscando...' : 'Buscar Canceladas en ERP'}
            </Button>

            {resultadoAuditoria?.detalle && resultadoAuditoria.detalle.length > 0 && (
              <Button
                variant="contained"
                onClick={handleAplicarCancelacionesSeleccionadas}
                disabled={selectedAuditoriaIds.size === 0 || aplicandoCancelaciones}
                startIcon={aplicandoCancelaciones ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
                sx={{
                  bgcolor: '#dc2626',
                  color: '#ffffff',
                  fontWeight: 800,
                  borderRadius: '6px',
                  textTransform: 'none',
                  fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                  height: 'clamp(26px, 2.2vw, 30px)',
                  px: 1.8,
                  '&:hover': { bgcolor: '#b91c1c' },
                  '&.Mui-disabled': { bgcolor: '#fca5a5', color: '#ffffff' }
                }}
              >
                {aplicandoCancelaciones
                  ? 'Cancelando...'
                  : `Marcar Seleccionadas como Canceladas (${selectedAuditoriaIds.size})`}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* MODAL ESTADO DE TABLAS BD */}
      <Dialog
        open={modalTablasOpen}
        onClose={() => setModalTablasOpen(false)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            px: 'clamp(12px, 1.5vw, 18px)',
            py: 'clamp(8px, 1vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 'clamp(0.82rem, 0.78rem + 0.18vw, 0.98rem)' }}>
            Estado de Tablas en Base de Datos MySQL
          </Typography>
          <IconButton size="small" onClick={() => setModalTablasOpen(false)} sx={{ color: '#ffffff', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 'clamp(12px, 1.5vw, 18px)', bgcolor: '#ffffff' }}>
          {loadingTablas ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}>
              <CircularProgress size={26} sx={{ color: '#0655A5' }} />
            </Box>
          ) : (
            <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: '7px', overflow: 'hidden', mt: 0.5, maxHeight: 300, overflowY: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Tabla</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Total Filas</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Datos (KB)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Índices (KB)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tablasBD.map((tab) => (
                    <TableRow key={tab.name}>
                      <TableCell sx={{ fontWeight: 700, color: '#334155', fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.5 }}>{tab.name}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.5 }}>{Number(tab.rows || 0).toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', py: 0.5 }}>{Number(tab.dataSizeKB || 0).toLocaleString()} KB</TableCell>
                      <TableCell align="right" sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', py: 0.5 }}>{Number(tab.indexSizeKB || 0).toLocaleString()} KB</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </DialogContent>
      </Dialog>

      {/* ── MODAL ALINEACIÓN DE CENTROS DE COSTO (ERP vs SOLICITUDES) ── */}
      <Dialog
        open={modalCCOpen}
        onClose={() => !actualizandoCC && setModalCCOpen(false)}
        maxWidth="lg"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
            color: '#ffffff',
            px: 'clamp(12px, 1.5vw, 18px)',
            py: 'clamp(8px, 1vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 'clamp(0.82rem, 0.78rem + 0.18vw, 0.98rem)' }}>
              Alineación de Centros de Costo (ERP vs Solicitudes)
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)' }}>
              Compara el CC actual en solicitudes contra la OC en el ERP y el catálogo tblcentrocosto
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => !actualizandoCC && setModalCCOpen(false)} sx={{ color: '#ffffff', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 'clamp(12px, 1.5vw, 18px)', bgcolor: '#ffffff' }}>
          <Stack spacing={1.5} sx={{ mt: 0.8 }}>
            {/* BARRA DE CONTROLES */}
            <Paper
              elevation={0}
              sx={{
                p: 1.2,
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flexGrow: 1 }}>
                <FormControl size="small" sx={{ ...elegantInputSx, minWidth: 200 }}>
                  <InputLabel>Empresa</InputLabel>
                  <Select
                    value={idEmpresaCCSelected}
                    onChange={(e) => setIdEmpresaCCSelected(e.target.value)}
                    label="Empresa"
                  >
                    <MenuItem value="ALL">TODAS LAS EMPRESAS</MenuItem>
                    {empresasDisponibles.map((emp) => (
                      <MenuItem key={emp.EMPRESAID} value={emp.EMPRESAID}>
                        {emp.NOMBRE_EMPRESA}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  size="small"
                  placeholder="Buscar folio, proveedor, CC..."
                  value={busquedaCC}
                  onChange={(e) => setBusquedaCC(e.target.value)}
                  sx={{ ...elegantInputSx, minWidth: 220 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: '#64748b' }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleConsultarDiscrepanciasCC}
                  disabled={loadingCC || actualizandoCC}
                  startIcon={loadingCC ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
                  sx={{
                    borderColor: '#0284c7',
                    color: '#0284c7',
                    fontWeight: 700,
                    textTransform: 'none',
                    height: '36px',
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.04)', borderColor: '#0284c7' }
                  }}
                >
                  Buscar Discrepancias
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleActualizarCCSeleccionados}
                  disabled={selectedCCIds.size === 0 || actualizandoCC || loadingCC}
                  startIcon={actualizandoCC ? <CircularProgress size={14} sx={{ color: '#ffffff' }} /> : <CheckCircleIcon fontSize="small" />}
                  sx={{
                    bgcolor: '#16a34a',
                    '&:hover': { bgcolor: '#15803d' },
                    color: '#ffffff',
                    fontWeight: 800,
                    textTransform: 'none',
                    height: '36px',
                    borderRadius: '8px',
                    px: 2,
                    boxShadow: '0 2px 6px rgba(22, 163, 74, 0.25)'
                  }}
                >
                  {actualizandoCC ? 'Actualizando...' : `Actualizar Seleccionados (${selectedCCIds.size})`}
                </Button>
              </Stack>
            </Paper>

            {/* RESUMEN DE ESTADO */}
            {discrepanciasCC.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, color: '#334155', fontSize: '0.78rem' }}>
                  Mostrando {discrepanciasFiltradas.length} de {discrepanciasCC.length} solicitud(es) con discrepancia de CC
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
                  {selectedCCIds.size} seleccionada(s) para actualizar
                </Typography>
              </Box>
            )}

            {/* TABLA DE RESULTADOS */}
            {loadingCC ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: '#0284c7' }} />
              </Box>
            ) : discrepanciasFiltradas.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: '8px', fontSize: '0.78rem' }}>
                {discrepanciasCC.length === 0
                  ? 'Haz clic en "Buscar Discrepancias" para consultar si existen solicitudes de pago desalineadas con respecto al ERP.'
                  : 'No se encontraron solicitudes que coincidan con la búsqueda actual.'}
              </Alert>
            ) : (
              <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', maxHeight: 460, overflowY: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox" sx={{ bgcolor: '#f8fafc', py: 0.6 }}>
                        <Checkbox
                          size="small"
                          indeterminate={selectedCCIds.size > 0 && selectedCCIds.size < discrepanciasFiltradas.filter(i => i.ID_NUEVO != null).length}
                          checked={discrepanciasFiltradas.filter(i => i.ID_NUEVO != null).length > 0 && selectedCCIds.size === discrepanciasFiltradas.filter(i => i.ID_NUEVO != null).length}
                          onChange={handleToggleSelectAllCC}
                          sx={{ color: '#0284c7', '&.Mui-checked': { color: '#0284c7' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: '#f8fafc', py: 0.6 }}>Folio OC / Solicitud</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: '#f8fafc', py: 0.6 }}>Empresa</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: '#f8fafc', py: 0.6 }}>Proveedor / Monto</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: '#f8fafc', py: 0.6 }}>CC Actual en Solicitud</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: '#f8fafc', py: 0.6 }}>CC en ERP (Fotografía)</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: '#f8fafc', py: 0.6 }}>Nuevo CC a Asignar</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.72rem', bgcolor: '#f8fafc', py: 0.6 }}>Estatus</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {discrepanciasFiltradas.map((row) => {
                      const isSelected = selectedCCIds.has(row.AUTSOLPAGOID);
                      const hasIdNuevo = row.ID_NUEVO != null;

                      return (
                        <TableRow
                          key={row.AUTSOLPAGOID}
                          hover
                          selected={isSelected}
                          sx={{
                            '&.Mui-selected': { bgcolor: 'rgba(2, 132, 199, 0.06)' },
                            '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.03)' }
                          }}
                        >
                          <TableCell padding="checkbox" sx={{ py: 0.6 }}>
                            <Checkbox
                              size="small"
                              checked={isSelected}
                              disabled={!hasIdNuevo}
                              onChange={() => handleToggleSelectOneCC(row.AUTSOLPAGOID)}
                              sx={{ color: '#0284c7', '&.Mui-checked': { color: '#0284c7' } }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.75rem' }}>
                              OC: {row.FOLIO_OC}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.65rem' }}>
                              ID Solicitud: #{row.AUTSOLPAGOID} ({row.FECHASOLICITUD || 'S/F'})
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            <Chip
                              label={row.EMPRESA || `ID ${row.EMPRESAID}`}
                              size="small"
                              sx={{
                                ...getEmpresaChipSx(row.EMPRESA),
                                height: '20px',
                                fontSize: '0.62rem'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', fontSize: '0.72rem', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.PROVEEDOR || '—'}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#0369a1', fontWeight: 700, fontSize: '0.68rem' }}>
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: row.DIVISA || 'MXN' }).format(row.MONTO_SOLICITADO || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            <Box sx={{ bgcolor: '#fee2e2', color: '#991b1b', p: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                              <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.68rem', display: 'block' }}>
                                {row.NUMERO_CC_ACTUAL || '(Sin CC Asignado)'}
                              </Typography>
                            </Box>
                            {row.NOMBRE_CC_ACTUAL && (
                              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.64rem', display: 'block', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {row.NOMBRE_CC_ACTUAL}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            <Box sx={{ bgcolor: '#f1f5f9', color: '#334155', p: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.68rem', display: 'block' }}>
                                {row.NUMERO_CC_ERP || '—'}
                              </Typography>
                            </Box>
                            {row.NOMBRE_CC_ERP && (
                              <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.64rem', display: 'block', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {row.NOMBRE_CC_ERP}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 0.6 }}>
                            {hasIdNuevo ? (
                              <Box>
                                <Box sx={{ bgcolor: '#dcfce7', color: '#166534', p: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.68rem', display: 'block' }}>
                                    {row.NUMERO_CC_NUEVO} (ID: {row.ID_NUEVO})
                                  </Typography>
                                </Box>
                                {row.NOMBRE_CC_NUEVO && (
                                  <Typography variant="caption" sx={{ color: '#15803d', fontSize: '0.64rem', display: 'block', maxWidth: 180, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {row.NOMBRE_CC_NUEVO}
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Chip
                                label="No existe en catálogo"
                                size="small"
                                sx={{
                                  bgcolor: '#fef3c7',
                                  color: '#92400e',
                                  fontWeight: 800,
                                  fontSize: '0.62rem',
                                  height: '20px'
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ py: 0.6 }}>
                            {hasIdNuevo ? (
                              <Chip
                                label="Listo"
                                size="small"
                                icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />}
                                sx={{
                                  bgcolor: '#e0f2fe',
                                  color: '#0369a1',
                                  fontWeight: 800,
                                  fontSize: '0.62rem',
                                  height: '20px'
                                }}
                              />
                            ) : (
                              <Tooltip title={`El CC "${row.NUMERO_CC_ERP}" debe darse de alta en el catálogo tblcentrocosto para la empresa correspondiente.`}>
                                <Chip
                                  label="Requiere Alta"
                                  size="small"
                                  icon={<WarningAmberIcon sx={{ fontSize: '13px !important' }} />}
                                  sx={{
                                    bgcolor: '#fee2e2',
                                    color: '#b91c1c',
                                    fontWeight: 800,
                                    fontSize: '0.62rem',
                                    height: '20px'
                                  }}
                                />
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setModalCCOpen(false)}
            disabled={actualizandoCC}
            sx={{ textTransform: 'none', fontWeight: 700, color: '#64748b' }}
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            onClick={handleActualizarCCSeleccionados}
            disabled={selectedCCIds.size === 0 || actualizandoCC || loadingCC}
            startIcon={actualizandoCC ? <CircularProgress size={14} sx={{ color: '#ffffff' }} /> : <CheckCircleIcon fontSize="small" />}
            sx={{
              bgcolor: '#16a34a',
              '&:hover': { bgcolor: '#15803d' },
              color: '#ffffff',
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: '8px',
              px: 2.5
            }}
          >
            {actualizandoCC ? 'Actualizando...' : `Actualizar Seleccionados (${selectedCCIds.size})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MODAL GESTIÓN DE RESPALDOS Y DESCARGAS ── */}
      <Dialog
        open={modalRespaldosOpen}
        onClose={() => !generandoRespaldoDB && !generandoRespaldoArchivos && setModalRespaldosOpen(false)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            px: 'clamp(12px, 1.5vw, 18px)',
            py: 'clamp(8px, 1vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 'clamp(0.82rem, 0.78rem + 0.18vw, 0.98rem)' }}>
              Gestor de Respaldos y Seguridad
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)' }}>
              Genera copias comprimidas (.zip) de la Base de Datos MySQL y de la carpeta de archivos / facturas
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setModalRespaldosOpen(false)} sx={{ color: '#ffffff', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 'clamp(12px, 1.5vw, 18px)', bgcolor: '#ffffff' }}>
          <Stack spacing={1.5} sx={{ mt: 0.8 }}>
            {/* Botones de acción para generar respaldos inmediatos */}
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.78rem)', fontWeight: 800, color: '#0f172a', mb: 1 }}>
                Generar Respaldo Manual Inmediato
              </Typography>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleGenerarRespaldoBD}
                    disabled={generandoRespaldoDB || generandoRespaldoArchivos}
                    startIcon={generandoRespaldoDB ? <CircularProgress size={14} color="inherit" /> : <StorageIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                      color: '#ffffff',
                      fontWeight: 800,
                      textTransform: 'none',
                      borderRadius: '7px',
                      fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                      py: 0.8
                    }}
                  >
                    {generandoRespaldoDB ? 'Generando Dump SQL...' : 'Respaldar Base de Datos MySQL (.zip)'}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleGenerarRespaldoArchivos}
                    disabled={generandoRespaldoDB || generandoRespaldoArchivos}
                    startIcon={generandoRespaldoArchivos ? <CircularProgress size={14} color="inherit" /> : <FolderZipIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      borderColor: '#0284c7',
                      color: '#0284c7',
                      fontWeight: 800,
                      textTransform: 'none',
                      borderRadius: '7px',
                      fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)',
                      py: 0.8,
                      '&:hover': { bgcolor: '#f0f9ff', borderColor: '#0369a1' }
                    }}
                  >
                    {generandoRespaldoArchivos ? 'Comprimiendo Archivos...' : 'Respaldar Carpeta de Archivos (.zip)'}
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {/* Barra de control de respaldos y retención de días */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
              <Typography sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.78rem)', fontWeight: 800, color: '#0f172a' }}>
                Respaldos Disponibles en el Servidor ({listaRespaldos.length})
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: '#f8fafc', p: '2px 8px', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#64748b', fontWeight: 700 }}>
                    Retención:
                  </Typography>
                  <Select
                    size="small"
                    value={diasRetencion}
                    onChange={(e) => handleCambiarDiasRetencion(e.target.value)}
                    variant="standard"
                    disableUnderline
                    sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#0f172a' }}
                  >
                    <MenuItem value={3}>3 días</MenuItem>
                    <MenuItem value={7}>7 días (Recomendado)</MenuItem>
                    <MenuItem value={15}>15 días</MenuItem>
                    <MenuItem value={30}>30 días</MenuItem>
                    <MenuItem value={60}>60 días</MenuItem>
                  </Select>
                  <Button
                    size="small"
                    onClick={handleLimpiarRespaldosAntiguos}
                    disabled={limpiandoAntiguos}
                    sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', textTransform: 'none', color: '#d97706', fontWeight: 800, p: 0.2 }}
                  >
                    {limpiandoAntiguos ? 'Depurando...' : `Depurar > ${diasRetencion}d`}
                  </Button>
                </Box>

                <Button
                  size="small"
                  onClick={handleCargarRespaldos}
                  disabled={loadingRespaldos}
                  startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', textTransform: 'none', fontWeight: 700 }}
                >
                  Actualizar Lista
                </Button>
              </Box>
            </Box>

            {loadingRespaldos ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} sx={{ color: '#0655A5' }} />
              </Box>
            ) : listaRespaldos.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: '7px', fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)' }}>
                Aún no hay respaldos generados. Haz clic en uno de los botones superiores para crear el primero.
              </Alert>
            ) : (
              <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: '7px', overflow: 'hidden', maxHeight: 280, overflowY: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Tipo</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Nombre del Archivo</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Tamaño</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9' }}>Fecha de Creación</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.6, bgcolor: '#f1f5f9', width: 140 }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {listaRespaldos.map((resp) => (
                      <TableRow key={resp.nombre} hover>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip
                            label={resp.tipo === 'db' ? 'Base de Datos' : 'Archivos'}
                            size="small"
                            sx={{
                              bgcolor: resp.tipo === 'db' ? '#e0f2fe' : '#f0fdf4',
                              color: resp.tipo === 'db' ? '#0369a1' : '#166534',
                              fontWeight: 800,
                              fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.65rem)',
                              height: 18
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#0f172a', fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.72rem)', py: 0.5 }}>
                          {resp.nombre}
                        </TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#64748b', py: 0.5 }}>
                          {resp.tamanoMB}
                        </TableCell>
                        <TableCell sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)', color: '#64748b', py: 0.5 }}>
                          {resp.fechaModificacion}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Descargar archivo .zip" arrow>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleDescargarRespaldo(resp)}
                                startIcon={<DownloadIcon sx={{ fontSize: 13 }} />}
                                sx={{
                                  background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                                  color: '#ffffff',
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)',
                                  height: 22,
                                  px: 0.8,
                                  borderRadius: '5px'
                                }}
                              >
                                Descargar
                              </Button>
                            </Tooltip>
                            <Tooltip title="Eliminar este archivo de respaldo (Local + Servidor + Bitácora)" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleEliminarRespaldo(resp)}
                                sx={{ color: '#ef4444', p: 0.3 }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc' }}>
          <Button
            onClick={() => setModalRespaldosOpen(false)}
            sx={{ textTransform: 'none', color: '#64748b', fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)' }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MODAL PRINCIPAL: PROGRAMADOR DE RUTINAS Y TAREAS (CRON) ── */}
      <Dialog
        open={modalTareasOpen}
        onClose={() => setModalTareasOpen(false)}
        maxWidth="lg"
        fullWidth
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            px: 'clamp(12px, 1.4vw, 18px)',
            py: 'clamp(10px, 1.1vw, 14px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ p: 0.8, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlarmOnIcon sx={{ fontSize: 'clamp(18px, 1.3vw, 24px)', color: '#ffffff' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 'clamp(0.84rem, 0.80rem + 0.20vw, 1.02rem)', color: '#ffffff', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                Programador de Rutinas y Tareas Automáticas (24/7)
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.66rem)', display: 'block', mt: 0.2 }}>
                Configura cuándo se ejecutan los respaldos, sincronizaciones y auto-alineaciones en segundo plano
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => setModalTareasOpen(false)}
            sx={{
              color: '#ffffff',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              borderRadius: '6px',
              p: 0.6
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 'clamp(12px, 1.4vw, 18px)', bgcolor: '#f8fafc' }}>
          <Stack spacing={1.5} sx={{ mt: 0.6 }}>
            {/* Barra de herramientas */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, px: 0.5 }}>
              <Typography sx={{ fontSize: 'clamp(0.68rem, 0.65rem + 0.08vw, 0.78rem)', fontWeight: 800, color: '#0f172a' }}>
                Rutinas Configuradas ({listaTareas.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={handleCargarTareas}
                  disabled={loadingTareas}
                  startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)',
                    textTransform: 'none',
                    fontWeight: 700,
                    color: '#0655A5'
                  }}
                >
                  Actualizar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAbrirCrearTarea}
                  startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                    color: '#ffffff',
                    fontWeight: 800,
                    textTransform: 'none',
                    borderRadius: '6px',
                    fontSize: 'clamp(0.60rem, 0.58rem + 0.06vw, 0.70rem)',
                    boxShadow: '0 2px 6px rgba(6, 85, 165, 0.3)',
                    px: 1.4,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #05488c 0%, #0655A5 100%)'
                    }
                  }}
                >
                  Nueva Rutina
                </Button>
              </Box>
            </Box>

            {loadingTareas ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={26} sx={{ color: '#0655A5' }} />
              </Box>
            ) : listaTareas.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: '8px', fontSize: 'clamp(0.62rem, 0.60rem + 0.06vw, 0.72rem)' }}>
                No hay rutinas programadas actualmente. Haz clic en "Nueva Rutina" para programar una tarea automática.
              </Alert>
            ) : (
              <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', bgcolor: '#ffffff' }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#334155', py: 'clamp(6px, 0.6vh, 8px)', width: 75 }}>Estado</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#334155', py: 'clamp(6px, 0.6vh, 8px)' }}>Nombre de la Rutina</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#334155', py: 'clamp(6px, 0.6vh, 8px)' }}>Acción Ejecutada</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#334155', py: 'clamp(6px, 0.6vh, 8px)' }}>Frecuencia / Horario</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#334155', py: 'clamp(6px, 0.6vh, 8px)' }}>Última Ejecución</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#334155', py: 'clamp(6px, 0.6vh, 8px)', width: 170 }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {listaTareas.map((tarea) => {
                      const isActivo = Number(tarea.ACTIVO) === 1;
                      const isExecuting = ejecutandoTareaId === tarea.IDTAREA;

                      let accionLabel = 'Respaldo BD';
                      let accionColor = { bg: '#e0f2fe', text: '#0369a1' };

                      if (tarea.ACCION === 'ALINEAR_CC') {
                        accionLabel = 'Auto-Alinear CC';
                        accionColor = { bg: '#f0fdf4', text: '#166534' };
                      } else if (tarea.ACCION === 'RESPALDO_ARCHIVOS') {
                        accionLabel = 'Respaldo Archivos';
                        accionColor = { bg: '#fef3c7', text: '#92400e' };
                      } else if (tarea.ACCION === 'AUDITAR_CANCELADAS') {
                        accionLabel = 'Auditoría / Limpieza';
                        accionColor = { bg: '#f3e8ff', text: '#6b21a8' };
                      }

                      return (
                        <TableRow key={tarea.IDTAREA} hover sx={{ opacity: isActivo ? 1 : 0.65, '&:hover': { bgcolor: '#f8fafc' }, borderBottom: '1px solid #f1f5f9' }}>
                          <TableCell align="center" sx={{ py: 'clamp(5px, 0.5vh, 7px)' }}>
                            <Tooltip title={isActivo ? 'Rutina activa (Haz clic para pausar)' : 'Rutina pausada (Haz clic para activar)'} arrow>
                              <Switch
                                size="small"
                                checked={isActivo}
                                onChange={() => handleToggleTarea(tarea)}
                                color="primary"
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ py: 'clamp(5px, 0.5vh, 7px)' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.60rem, 0.58rem + 0.06vw, 0.70rem)', color: '#0f172a' }}>
                              {tarea.NOMBRE}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 'clamp(5px, 0.5vh, 7px)' }}>
                            <Chip
                              label={accionLabel}
                              size="small"
                              sx={{
                                bgcolor: accionColor.bg,
                                color: accionColor.text,
                                fontWeight: 800,
                                fontSize: 'clamp(0.54rem, 0.52rem + 0.05vw, 0.62rem)',
                                height: 18,
                                borderRadius: '5px'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ py: 'clamp(5px, 0.5vh, 7px)' }}>
                            <Tooltip title={`Expresión Cron: ${tarea.EXPRESION_CRON}`} arrow>
                              <Chip
                                label={tarea.FRECUENCIA_TEXTO || tarea.EXPRESION_CRON}
                                size="small"
                                icon={<ScheduleIcon sx={{ fontSize: '12px !important' }} />}
                                sx={{
                                  bgcolor: '#f1f5f9',
                                  color: '#334155',
                                  fontWeight: 700,
                                  fontSize: 'clamp(0.54rem, 0.52rem + 0.05vw, 0.62rem)',
                                  height: 19,
                                  borderRadius: '5px'
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ py: 'clamp(5px, 0.5vh, 7px)' }}>
                            <Box>
                              <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.65rem)', color: '#64748b', fontWeight: 600 }}>
                                {tarea.ULTIMA_EJECUCION ? new Date(tarea.ULTIMA_EJECUCION).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' }) : 'Pendiente de inicio'}
                              </Typography>
                              {tarea.ULTIMO_RESULTADO && (
                                <Tooltip title={tarea.ULTIMO_RESULTADO} arrow>
                                  <Typography sx={{ fontSize: 'clamp(0.52rem, 0.50rem + 0.05vw, 0.60rem)', color: '#16a34a', maxWidth: 170, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {tarea.ULTIMO_RESULTADO}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ py: 'clamp(5px, 0.5vh, 7px)' }}>
                            <Stack direction="row" spacing={0.4} justifyContent="center">
                              <Tooltip title="Ejecutar esta rutina ahora mismo" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEjecutarTareaManual(tarea)}
                                  disabled={isExecuting}
                                  sx={{ color: '#16a34a', p: 0.4, bgcolor: 'rgba(22, 163, 74, 0.08)', '&:hover': { bgcolor: 'rgba(22, 163, 74, 0.18)' }, borderRadius: '5px' }}
                                >
                                  {isExecuting ? <CircularProgress size={13} color="inherit" /> : <PlayArrowIcon sx={{ fontSize: 15 }} />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Editar horario o acción" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleAbrirEditarTarea(tarea)}
                                  sx={{ color: '#0284c7', p: 0.4, bgcolor: 'rgba(2, 132, 199, 0.08)', '&:hover': { bgcolor: 'rgba(2, 132, 199, 0.18)' }, borderRadius: '5px' }}
                                >
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar rutina" arrow>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEliminarTarea(tarea)}
                                  sx={{ color: '#ef4444', p: 0.4, bgcolor: 'rgba(239, 68, 68, 0.08)', '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.18)' }, borderRadius: '5px' }}
                                >
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Paper>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            variant="outlined"
            onClick={() => setModalTareasOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#475569',
              borderColor: '#cbd5e1',
              fontWeight: 700,
              fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
              borderRadius: '6px',
              px: 2,
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MODAL: FORMULARIO CREAR / EDITAR RUTINA ── */}
      <Dialog
        open={modalEditarTareaOpen}
        onClose={() => !guardandoTarea && setModalEditarTareaOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
            border: '1px solid #e2e8f0'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            px: 'clamp(12px, 1.4vw, 18px)',
            py: 'clamp(10px, 1.1vw, 14px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ p: 0.6, borderRadius: '6px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 'clamp(16px, 1.2vw, 20px)', color: '#ffffff' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 'clamp(0.82rem, 0.78rem + 0.18vw, 0.98rem)', color: '#ffffff' }}>
              {tareaEnEdicion ? 'Editar Rutina Programada' : 'Crear Nueva Rutina Automática'}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setModalEditarTareaOpen(false)}
            sx={{
              color: '#ffffff',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              borderRadius: '6px',
              p: 0.5
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 'clamp(12px, 1.5vw, 18px)', bgcolor: '#ffffff' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Nombre */}
            <TextField
              label="Nombre de la Rutina"
              placeholder="Ej. Respaldo Diario de Base de Datos"
              value={formTarea.nombre}
              onChange={(e) => setFormTarea(prev => ({ ...prev, nombre: e.target.value }))}
              fullWidth
              size="small"
              sx={elegantInputSx}
            />

            {/* Acción */}
            <FormControl fullWidth size="small" sx={elegantInputSx}>
              <InputLabel>Acción a Realizar</InputLabel>
              <Select
                value={formTarea.accion}
                label="Acción a Realizar"
                onChange={(e) => setFormTarea(prev => ({ ...prev, accion: e.target.value }))}
              >
                <MenuItem value="RESPALDO_BD">💾 Respaldo de Base de Datos MySQL (.zip)</MenuItem>
                <MenuItem value="ALINEAR_CC">🔄 Auto-Alineación de Centros de Costo (ERP vs Solicitudes)</MenuItem>
                <MenuItem value="RESPALDO_ARCHIVOS">📦 Respaldo de Carpeta de Archivos y Comprobantes (.zip)</MenuItem>
                <MenuItem value="AUDITAR_CANCELADAS">🧹 Mantenimiento Nocturno y Limpieza de Respaldos Viejos</MenuItem>
              </Select>
            </FormControl>

            {/* Tipo de Frecuencia */}
            <FormControl fullWidth size="small" sx={elegantInputSx}>
              <InputLabel>Frecuencia de Ejecución</InputLabel>
              <Select
                value={formTarea.tipoFrecuencia}
                label="Frecuencia de Ejecución"
                onChange={(e) => setFormTarea(prev => ({ ...prev, tipoFrecuencia: e.target.value }))}
              >
                <MenuItem value="DIARIO">📅 Una vez al día (Hora fija)</MenuItem>
                <MenuItem value="VARIOS_HORARIOS">⏰ Múltiples horarios al día (Selecciona las horas con botones)</MenuItem>
                <MenuItem value="CADA_HORAS">⏱️ Cada cierto número de horas</MenuItem>
                <MenuItem value="DIAS_PERSONALIZADOS">📆 Días específicos de la semana (con horario)</MenuItem>
                <MenuItem value="MANUAL">⚙️ Avanzado (Código Cron para programadores)</MenuItem>
              </Select>
            </FormControl>

            {/* 1. UNA VEZ AL DÍA */}
            {formTarea.tipoFrecuencia === 'DIARIO' && (
              <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.06vw, 0.72rem)', fontWeight: 700, color: '#334155', mb: 1 }}>
                  Selecciona la hora exacta a la que se ejecutará todos los días:
                </Typography>
                <TextField
                  label="Hora de Ejecución"
                  type="time"
                  value={formTarea.horaFija}
                  onChange={(e) => setFormTarea(prev => ({ ...prev, horaFija: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={elegantInputSx}
                />
              </Paper>
            )}

            {/* 2. MÚLTIPLES HORARIOS AL DÍA (SELECTOR VISUAL DE HORAS) */}
            {formTarea.tipoFrecuencia === 'VARIOS_HORARIOS' && (
              <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)', fontWeight: 800, color: '#0f172a', mb: 0.5 }}>
                  Haz clic en las horas del día en que deseas que se ejecute:
                </Typography>
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', color: '#64748b', mb: 1.2 }}>
                  Puedes marcar tantas horas como quieras (se iluminarán en azul).
                </Typography>

                {/* Botones de combinaciones comunes */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 800, color: '#475569', mb: 0.5 }}>
                    Combinaciones Rápidas:
                  </Typography>
                  <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap>
                    <Chip
                      label="Mañana y Tarde (8 AM, 2 PM)"
                      size="small"
                      clickable
                      onClick={() => setFormTarea(prev => ({ ...prev, horasMultiples: [8, 14] }))}
                      sx={{ fontSize: '0.62rem', fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1' }}
                    />
                    <Chip
                      label="3 veces al día (8 AM, 2 PM, 8 PM)"
                      size="small"
                      clickable
                      onClick={() => setFormTarea(prev => ({ ...prev, horasMultiples: [8, 14, 20] }))}
                      sx={{ fontSize: '0.62rem', fontWeight: 700, bgcolor: '#dbeafe', color: '#1d4ed8' }}
                    />
                    <Chip
                      label="4 veces al día (6 AM, 12 PM, 6 PM, 12 AM)"
                      size="small"
                      clickable
                      onClick={() => setFormTarea(prev => ({ ...prev, horasMultiples: [0, 6, 12, 18] }))}
                      sx={{ fontSize: '0.62rem', fontWeight: 700, bgcolor: '#f3e8ff', color: '#6b21a8' }}
                    />
                    <Chip
                      label="Horario Laboral (8, 10, 12, 14, 16, 18)"
                      size="small"
                      clickable
                      onClick={() => setFormTarea(prev => ({ ...prev, horasMultiples: [8, 10, 12, 14, 16, 18] }))}
                      sx={{ fontSize: '0.62rem', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }}
                    />
                  </Stack>
                </Box>

                {/* Rejilla de las 24 horas del día */}
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', fontWeight: 800, color: '#475569', mb: 0.6 }}>
                  Horas del Día (00:00 a 23:00):
                </Typography>
                <Grid container spacing={0.6}>
                  {Array.from({ length: 24 }).map((_, h) => {
                    const isSelected = formTarea.horasMultiples.includes(h);
                    const ampm = h >= 12 ? 'PM' : 'AM';
                    const h12 = h % 12 === 0 ? 12 : h % 12;
                    const label = `${String(h12).padStart(2, '0')}:00 ${ampm}`;

                    return (
                      <Grid item xs={3} sm={2} key={h}>
                        <Button
                          fullWidth
                          size="small"
                          variant={isSelected ? 'contained' : 'outlined'}
                          onClick={() => {
                            setFormTarea(prev => {
                              const existe = prev.horasMultiples.includes(h);
                              const nuevas = existe
                                ? prev.horasMultiples.filter(item => item !== h)
                                : [...prev.horasMultiples, h];
                              return { ...prev, horasMultiples: nuevas };
                            });
                          }}
                          sx={{
                            fontSize: '0.58rem',
                            fontWeight: 800,
                            py: 0.4,
                            px: 0.2,
                            minWidth: 0,
                            borderRadius: '5px',
                            bgcolor: isSelected ? '#0655A5' : '#ffffff',
                            color: isSelected ? '#ffffff' : '#475569',
                            borderColor: isSelected ? '#0655A5' : '#cbd5e1',
                            '&:hover': {
                              bgcolor: isSelected ? '#0861B8' : '#f1f5f9'
                            }
                          }}
                        >
                          {label}
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Resumen en vivo */}
                <Box sx={{ mt: 1.5, p: 1, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px' }}>
                  <Typography sx={{ fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.68rem)', fontWeight: 800, color: '#166534' }}>
                    ✅ Resumen: Se ejecutará {formTarea.horasMultiples.length} vez/veces al día ({formTarea.horasMultiples.length > 0 ? formTarea.horasMultiples.slice().sort((a,b)=>a-b).map(h => `${h % 12 === 0 ? 12 : h % 12}:00 ${h >= 12 ? 'PM' : 'AM'}`).join(', ') : 'Ninguna hora seleccionada'}).
                  </Typography>
                </Box>
              </Paper>
            )}

            {/* 3. CADA CIERTO NÚMERO DE HORAS */}
            {formTarea.tipoFrecuencia === 'CADA_HORAS' && (
              <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <FormControl fullWidth size="small" sx={elegantInputSx}>
                  <InputLabel>Intervalo de Horas</InputLabel>
                  <Select
                    value={formTarea.cadaHoras}
                    label="Intervalo de Horas"
                    onChange={(e) => setFormTarea(prev => ({ ...prev, cadaHoras: e.target.value }))}
                  >
                    <MenuItem value="1">⏱️ Cada 1 hora (24 veces al día)</MenuItem>
                    <MenuItem value="2">⏱️ Cada 2 horas (12 veces al día)</MenuItem>
                    <MenuItem value="3">⏱️ Cada 3 horas (8 veces al día)</MenuItem>
                    <MenuItem value="4">⏱️ Cada 4 horas (6 veces al día)</MenuItem>
                    <MenuItem value="6">⏱️ Cada 6 horas (4 veces al día)</MenuItem>
                    <MenuItem value="12">⏱️ Cada 12 horas (2 veces al día)</MenuItem>
                  </Select>
                </FormControl>
              </Paper>
            )}

            {/* 4. DÍAS ESPECÍFICOS DE LA SEMANA */}
            {formTarea.tipoFrecuencia === 'DIAS_PERSONALIZADOS' && (
              <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <Typography sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.06vw, 0.72rem)', fontWeight: 800, color: '#0f172a', mb: 0.8 }}>
                  1. Selecciona los días de la semana:
                </Typography>

                {/* Botones de Días */}
                <Stack direction="row" spacing={0.6} sx={{ mb: 1.2 }}>
                  {[
                    { num: 1, label: 'Lun' },
                    { num: 2, label: 'Mar' },
                    { num: 3, label: 'Mié' },
                    { num: 4, label: 'Jue' },
                    { num: 5, label: 'Vie' },
                    { num: 6, label: 'Sáb' },
                    { num: 0, label: 'Dom' }
                  ].map((dia) => {
                    const isSel = formTarea.diasSemanaMultiples.includes(dia.num);
                    return (
                      <Button
                        key={dia.num}
                        size="small"
                        variant={isSel ? 'contained' : 'outlined'}
                        onClick={() => {
                          setFormTarea(prev => {
                            const existe = prev.diasSemanaMultiples.includes(dia.num);
                            const nuevos = existe
                              ? prev.diasSemanaMultiples.filter(d => d !== dia.num)
                              : [...prev.diasSemanaMultiples, dia.num];
                            return { ...prev, diasSemanaMultiples: nuevos };
                          });
                        }}
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.62rem',
                          minWidth: 36,
                          py: 0.5,
                          borderRadius: '6px',
                          bgcolor: isSel ? '#4f46e5' : '#ffffff',
                          color: isSel ? '#ffffff' : '#475569',
                          borderColor: isSel ? '#4f46e5' : '#cbd5e1'
                        }}
                      >
                        {dia.label}
                      </Button>
                    );
                  })}
                </Stack>

                <Typography sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.06vw, 0.72rem)', fontWeight: 800, color: '#0f172a', mb: 0.6, mt: 1.2 }}>
                  2. Hora de ejecución para esos días:
                </Typography>
                <TextField
                  label="Hora de Ejecución"
                  type="time"
                  value={formTarea.horaFija}
                  onChange={(e) => setFormTarea(prev => ({ ...prev, horaFija: e.target.value }))}
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  sx={elegantInputSx}
                />
              </Paper>
            )}

            {/* 5. MODO CÓDIGO CRON (PARA DESARROLLADORES) */}
            {formTarea.tipoFrecuencia === 'MANUAL' && (
              <Stack spacing={1.5}>
                <TextField
                  label="Expresión Cron (5 posiciones)"
                  placeholder="Ej. 0 8,14,20 * * *"
                  value={formTarea.expresionCronManual}
                  onChange={(e) => setFormTarea(prev => ({ ...prev, expresionCronManual: e.target.value }))}
                  fullWidth
                  size="small"
                  sx={elegantInputSx}
                />

                <Paper
                  elevation={0}
                  sx={{
                    p: 1.2,
                    bgcolor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '7px'
                  }}
                >
                  <Typography sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', fontWeight: 800, color: '#166534', mb: 0.2 }}>
                    💡 TRADUCCIÓN AUTOMÁTICA:
                  </Typography>
                  <Typography sx={{ fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)', fontWeight: 700, color: '#15803d' }}>
                    {describirExpresionCron(formTarea.expresionCronManual)}
                  </Typography>
                </Paper>
              </Stack>
            )}

            {/* Switch de Estado inicial */}
            <Paper elevation={0} sx={{ p: 1.2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)', fontWeight: 700, color: '#0f172a' }}>
                Habilitar rutina inmediatamente
              </Typography>
              <Switch
                size="small"
                checked={Number(formTarea.activo) === 1}
                onChange={(e) => setFormTarea(prev => ({ ...prev, activo: e.target.checked ? 1 : 0 }))}
                color="primary"
              />
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            variant="outlined"
            onClick={() => setModalEditarTareaOpen(false)}
            disabled={guardandoTarea}
            sx={{
              textTransform: 'none',
              color: '#475569',
              borderColor: '#cbd5e1',
              fontWeight: 700,
              fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
              borderRadius: '6px',
              px: 1.8,
              '&:hover': {
                borderColor: '#94a3b8',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardarTarea}
            disabled={guardandoTarea}
            startIcon={guardandoTarea ? <CircularProgress size={14} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
            sx={{
              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
              color: '#ffffff',
              fontWeight: 800,
              textTransform: 'none',
              borderRadius: '6px',
              fontSize: 'clamp(0.60rem, 0.58rem + 0.06vw, 0.70rem)',
              boxShadow: '0 2px 6px rgba(6, 85, 165, 0.3)',
              px: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #05488c 0%, #0655A5 100%)'
              }
            }}
          >
            {guardandoTarea ? 'Guardando...' : (tareaEnEdicion ? 'Actualizar Rutina' : 'Guardar y Programar')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* ── MODAL: PANEL DE CONTROL MAESTRO Y SUPERVISIÓN TOTAL DE SOLICITUDES ── */}
      <Dialog
        open={modalControlMaestroOpen}
        onClose={() => setModalControlMaestroOpen(false)}
        maxWidth="xl"
        fullWidth
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
            border: '1px solid #e2e8f0',
            height: '92vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
            color: '#ffffff',
            px: 'clamp(12px, 1.4vw, 18px)',
            py: 'clamp(10px, 1.1vw, 14px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Box sx={{ p: 0.8, borderRadius: '8px', bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AdminPanelSettingsIcon sx={{ fontSize: 'clamp(20px, 1.5vw, 26px)', color: '#ffffff' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 'clamp(0.85rem, 0.82rem + 0.20vw, 1.05rem)', color: '#ffffff', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
                Panel de Control Maestro y Supervisión Total de Solicitudes
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.66rem)', display: 'block', mt: 0.2 }}>
                Edición de montos con recálculo automático de saldos en <code>tblpagosacum</code>, estatus, firmas, fechas y gestión de documentos
              </Typography>
            </Box>
          </Box>
          <IconButton
            size="small"
            onClick={() => setModalControlMaestroOpen(false)}
            sx={{
              color: '#ffffff',
              bgcolor: 'rgba(255,255,255,0.12)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
              borderRadius: '6px',
              p: 0.6
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 'clamp(10px, 1.2vw, 16px)', bgcolor: '#f8fafc', flexGrow: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {/* ── BARRA DE FILTROS AVANZADOS (Solo visible en modo búsqueda) ── */}
          {!solicitudSeleccionada && (
            <Paper elevation={0} sx={{ p: 1.8, border: '1px solid #cbd5e1', borderRadius: '10px', bgcolor: '#ffffff' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.2 }}>
                <Typography sx={{ fontSize: 'clamp(0.70rem, 0.68rem + 0.10vw, 0.82rem)', fontWeight: 800, color: '#0f172a' }}>
                  🔍 Filtros de Búsqueda y Supervisión de Solicitudes
                </Typography>
                <Typography sx={{ fontSize: '0.60rem', color: '#64748b' }}>
                  Filtra por empresa, estatus, folio, comprador o centro de costo
                </Typography>
              </Box>

              <Grid container spacing={1.2}>
                {/* 1. Empresa */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" sx={elegantInputSx}>
                    <InputLabel>Empresa</InputLabel>
                    <Select
                      value={empresaFiltroControl}
                      label="Empresa"
                      onChange={(e) => setEmpresaFiltroControl(e.target.value)}
                    >
                      <MenuItem value="todas">Todas las Empresas</MenuItem>
                      {(catalogosControl.empresas.length > 0 ? catalogosControl.empresas : empresasDisponibles).map((emp) => (
                        <MenuItem key={emp.EMPRESAID} value={emp.EMPRESAID}>
                          {emp.NOMBRE_CORTO || emp.NOMBRE_EMPRESA || emp.NOMBRE}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* 2. Estatus */}
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small" sx={elegantInputSx}>
                    <InputLabel>Estatus</InputLabel>
                    <Select
                      value={estatusFiltroControl}
                      label="Estatus"
                      onChange={(e) => setEstatusFiltroControl(e.target.value)}
                    >
                      <MenuItem value="todos">Todos los Estatus</MenuItem>
                      <MenuItem value="1">1. Pendiente</MenuItem>
                      <MenuItem value="2">2. En Proceso</MenuItem>
                      <MenuItem value="3">3. Autorizado</MenuItem>
                      <MenuItem value="4">4. Vencido</MenuItem>
                      <MenuItem value="5">5. Pagado</MenuItem>
                      <MenuItem value="6">6. Cancelado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* 3. Folio (Solicitud # / OC) */}
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Folio (# Solicitud / OC)"
                    placeholder="Ej. 6598 o 46130"
                    value={folioFiltroControl}
                    onChange={(e) => setFolioFiltroControl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleBuscarControlMaestro()}
                    sx={elegantInputSx}
                  />
                </Grid>

                {/* 4. Compradora */}
                <Grid item xs={12} sm={6} md={2.5}>
                  <FormControl fullWidth size="small" sx={elegantInputSx}>
                    <InputLabel>Compradora</InputLabel>
                    <Select
                      value={compradorFiltroControl}
                      label="Compradora"
                      onChange={(e) => setCompradorFiltroControl(e.target.value)}
                    >
                      <MenuItem value="todos">Todas las Compradoras</MenuItem>
                      {catalogosControl.compradores.map((comp, idx) => (
                        <MenuItem key={idx} value={comp}>{comp}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* 5. Centro de Costo */}
                <Grid item xs={12} sm={6} md={2.5}>
                  <FormControl fullWidth size="small" sx={elegantInputSx}>
                    <InputLabel>Centro de Costo</InputLabel>
                    <Select
                      value={centroCostoFiltroControl}
                      label="Centro de Costo"
                      onChange={(e) => setCentroCostoFiltroControl(e.target.value)}
                    >
                      <MenuItem value="todos">Todos los Centros</MenuItem>
                      {catalogosControl.centrosCosto.map((cc) => (
                        <MenuItem key={cc.CCID} value={cc.NUMERO_CC || cc.CCID}>
                          {cc.NUMERO_CC} - {cc.CENTRO_COSTO}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* 6. Departamento */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small" sx={elegantInputSx}>
                    <InputLabel>Departamento</InputLabel>
                    <Select
                      value={departamentoFiltroControl}
                      label="Departamento"
                      onChange={(e) => setDepartamentoFiltroControl(e.target.value)}
                    >
                      <MenuItem value="todos">Todos los Departamentos</MenuItem>
                      {catalogosControl.departamentos.map((dep) => (
                        <MenuItem key={dep.DEPARTAMENTOID} value={dep.NOMBRE_DEPARTAMENTO || dep.DEPARTAMENTOID}>
                          {dep.NOMBRE_DEPARTAMENTO}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* 7. Proveedor / Búsqueda General */}
                <Grid item xs={12} sm={6} md={4.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Proveedor / Texto Libre"
                    placeholder="Ej. INTERLACE, MATERIALES, etc..."
                    value={busquedaControl}
                    onChange={(e) => setBusquedaControl(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleBuscarControlMaestro()}
                    sx={elegantInputSx}
                  />
                </Grid>

                {/* 8. Botones de Acción de Búsqueda */}
                <Grid item xs={12} md={4.5} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleBuscarControlMaestro}
                    disabled={cargandoControl}
                    startIcon={cargandoControl ? <CircularProgress size={14} color="inherit" /> : <SearchIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                      fontWeight: 800,
                      textTransform: 'none',
                      borderRadius: '6px',
                      fontSize: 'clamp(0.62rem, 0.60rem + 0.06vw, 0.72rem)',
                      boxShadow: 'none',
                      py: 0.9
                    }}
                  >
                    {cargandoControl ? 'Buscando...' : `Buscar Solicitudes (${resultadosControl.length})`}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleLimpiarFiltrosControl}
                    sx={{
                      borderColor: '#cbd5e1',
                      color: '#475569',
                      fontWeight: 700,
                      fontSize: '0.62rem',
                      textTransform: 'none',
                      borderRadius: '6px',
                      whiteSpace: 'nowrap',
                      py: 0.9
                    }}
                  >
                    Limpiar
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          )}

          {/* ── CUERPO PRINCIPAL: LISTA DE RESULTADOS O EXPEDIENTE SELECCIONADO ── */}
          {!solicitudSeleccionada ? (
            /* VISTA A: TABLA COMPLETA DE RESULTADOS */
            <Paper elevation={0} sx={{ flexGrow: 1, border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#ffffff', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ p: 1.2, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 'clamp(0.66rem, 0.64rem + 0.06vw, 0.76rem)', fontWeight: 800, color: '#334155' }}>
                  📋 Resultados de Solicitudes Encontradas ({resultadosControl.length})
                </Typography>
                <Typography sx={{ fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)', color: '#64748b' }}>
                  Haz clic en "Editar / Administrar" sobre cualquier solicitud para abrir su expediente
                </Typography>
              </Box>

              <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                {cargandoControl ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size={32} sx={{ color: '#0655A5' }} />
                    <Typography sx={{ mt: 1.5, fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>Consultando solicitudes con los filtros aplicados...</Typography>
                  </Box>
                ) : resultadosControl.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
                    <SearchIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1 }} />
                    <Typography sx={{ fontSize: '0.74rem', color: '#475569', fontWeight: 700 }}>No hay solicitudes para mostrar</Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#94a3b8' }}>Ajusta los filtros arriba y presiona "Buscar Solicitudes"</Typography>
                  </Box>
                ) : (
                  <Table size="small" stickyHeader>
                    <TableHead sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 800, fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)', color: '#1e293b' } }}>
                      <TableRow>
                        <TableCell>Folio / OC</TableCell>
                        <TableCell>Empresa</TableCell>
                        <TableCell>Proveedor</TableCell>
                        <TableCell>Comprador</TableCell>
                        <TableCell>Centro de Costo</TableCell>
                        <TableCell>Monto</TableCell>
                        <TableCell>Fecha Sol.</TableCell>
                        <TableCell align="center">Estatus</TableCell>
                        <TableCell align="center">Acción</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultadosControl.map((item) => {
                        let estatusTexto = 'Pendiente';
                        let estatusColor = '#64748b';
                        const est = Number(item.ESTATUS);
                        if (est === 2) { estatusTexto = 'En Proceso'; estatusColor = '#0284c7'; }
                        else if (est === 3) { estatusTexto = 'Autorizado'; estatusColor = '#16a34a'; }
                        else if (est === 4) { estatusTexto = 'Vencido'; estatusColor = '#d97706'; }
                        else if (est === 5) { estatusTexto = 'Pagado'; estatusColor = '#7c3aed'; }
                        else if (est === 6) { estatusTexto = 'Cancelado'; estatusColor = '#dc2626'; }

                        return (
                          <TableRow key={item.AUTSOLPAGOID} hover sx={{ '&:hover': { bgcolor: 'rgba(6, 85, 165, 0.03)' } }}>
                            <TableCell sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#0655A5' }}>
                              #{item.AUTSOLPAGOID}
                              <Typography variant="caption" sx={{ display: 'block', color: '#0f172a', fontWeight: 800, fontSize: '0.58rem' }}>
                                OC: {item.FOLIO_OC}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.60rem' }}>
                              <Chip
                                label={item.EMPRESA_CORTO || item.EMPRESA_NOMBRE || `Emp ${item.EMPRESAID}`}
                                size="small"
                                sx={{ height: 20, fontSize: '0.56rem', fontWeight: 700, bgcolor: '#f1f5f9', color: '#334155', border: '1px solid #cbd5e1' }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {item.PROVEEDOR || '(Sin proveedor)'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.60rem', color: '#475569', fontWeight: 600 }}>
                              {item.COMPRADOR || '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.58rem', color: '#475569' }}>
                              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#0f172a' }}>
                                {item.NUMERO_CC ? `CC ${item.NUMERO_CC}` : (item.CENTRO_COSTO_NOMBRE || '—')}
                              </Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.52rem', color: '#64748b', display: 'block' }}>
                                {item.DEPARTAMENTO_NOMBRE || 'General'}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.64rem', fontWeight: 800, color: '#0f172a', whiteSpace: 'nowrap' }}>
                              {new Intl.NumberFormat('es-MX', { style: 'currency', currency: item.DIVISA || 'MXN' }).format(item.MONTO_SOLICITADO || 0)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.58rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                              {item.FECHA_SOLICITUD ? new Date(item.FECHA_SOLICITUD).toLocaleDateString('es-MX') : '—'}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={estatusTexto}
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: '0.56rem',
                                  fontWeight: 800,
                                  color: estatusColor,
                                  bgcolor: `${estatusColor}15`,
                                  border: `1px solid ${estatusColor}30`
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleSeleccionarSolicitudControl(item.AUTSOLPAGOID)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 800,
                                  fontSize: '0.58rem',
                                  background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                                  borderRadius: '5px',
                                  boxShadow: 'none',
                                  py: 0.3,
                                  px: 1.2
                                }}
                              >
                                Editar ⚙️
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Paper>
          ) : (
            /* VISTA B: EXPEDIENTE Y EDITOR MAESTRO COMPLETO (PANTALLA LIMPIA Y ESPACIOSA) */
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: '12px', bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
              {/* Encabezado del expediente con botón de retorno claro */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, pb: 1.5, mb: 1.5, borderBottom: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setSolicitudSeleccionada(null)}
                    startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 800,
                      fontSize: '0.66rem',
                      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                      color: '#ffffff',
                      borderRadius: '7px',
                      boxShadow: 'none',
                      px: 1.5,
                      py: 0.6
                    }}
                  >
                    ⬅️ Volver a Búsqueda y Filtros
                  </Button>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: 'clamp(0.85rem, 0.80rem + 0.20vw, 1.05rem)', color: '#0655A5' }}>
                      Expediente Solicitud #{solicitudSeleccionada.solicitud.AUTSOLPAGOID} • OC: {solicitudSeleccionada.solicitud.FOLIO_OC}
                    </Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#475569', mt: 0.2 }}>
                      Empresa: <strong>{solicitudSeleccionada.solicitud.EMPRESA_NOMBRE || `Empresa ${solicitudSeleccionada.solicitud.EMPRESAID}`}</strong> • Proveedor: <strong>{solicitudSeleccionada.solicitud.PROVEEDOR}</strong>
                    </Typography>
                  </Box>
                </Box>

                {/* Resumen de Saldo en Pagos Acumulados */}
                {solicitudSeleccionada.acumulado && (
                  <Paper elevation={0} sx={{ p: '6px 14px', bgcolor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '8px' }}>
                    <Typography sx={{ fontSize: '0.56rem', fontWeight: 800, color: '#166534', textTransform: 'uppercase' }}>
                      Saldo en OC {solicitudSeleccionada.solicitud.FOLIO_OC} (tblpagosacum)
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 900, color: '#15803d' }}>
                      Total OC: {new Intl.NumberFormat('es-MX', { style: 'currency', currency: solicitudSeleccionada.solicitud.DIVISA || 'MXN' }).format(solicitudSeleccionada.acumulado.TOTAL_OC || 0)} | Restante: {new Intl.NumberFormat('es-MX', { style: 'currency', currency: solicitudSeleccionada.solicitud.DIVISA || 'MXN' }).format(solicitudSeleccionada.acumulado.RESTANTE || 0)}
                    </Typography>
                  </Paper>
                )}
              </Box>

              {/* Pestañas Maestras */}
              <Tabs
                value={tabMaestro}
                onChange={(_, val) => setTabMaestro(val)}
                sx={{
                  minHeight: 38,
                  borderBottom: '2px solid #f1f5f9',
                  mb: 2,
                  '& .MuiTab-root': {
                    minHeight: 38,
                    py: 0.6,
                    px: 2,
                    fontSize: 'clamp(0.64rem, 0.62rem + 0.08vw, 0.74rem)',
                    fontWeight: 800,
                    textTransform: 'none'
                  }
                }}
              >
                <Tab label="📊 1. Montos, Estatus, Firmas y Fechas" />
                <Tab label={`📁 2. Documentos y Archivos (${solicitudSeleccionada.documentos?.length || 0})`} />
                <Tab label={`📜 3. Historial y Auditoría (${(solicitudSeleccionada.historialAut?.length || 0) + (solicitudSeleccionada.bitacora?.length || 0)})`} />
              </Tabs>

              {/* ── CONTENIDO TAB 0: DASHBOARD EJECUTIVO EN 2 COLUMNAS PERFECTAMENTE ORGANIZADAS ── */}
              {tabMaestro === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1, overflowY: 'auto', p: 0.5 }}>
                  <Grid container spacing={2}>
                    {/* ══ COLUMNA IZQUIERDA: MONTO, RECÁLCULO Y ESTATUS ══ */}
                    <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* 1. SECCIÓN: MONTO SOLICITADO Y RECÁLCULO */}
                      <Paper elevation={0} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: '10px', bgcolor: '#ffffff' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <MonetizationOnIcon sx={{ color: '#0655A5', fontSize: 22 }} />
                          <Typography sx={{ fontWeight: 900, fontSize: '0.76rem', color: '#0f172a' }}>
                            1. Monto Solicitado y Recálculo de Saldo
                          </Typography>
                        </Box>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Monto Solicitado"
                              type="number"
                              value={formEdicionMaestra.montoSolicitado}
                              onChange={(e) => setFormEdicionMaestra(prev => ({ ...prev, montoSolicitado: e.target.value }))}
                              fullWidth
                              size="small"
                              sx={{
                                ...elegantInputSx,
                                '& input': { fontWeight: 900, fontSize: '0.94rem', color: '#0655A5' }
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <Typography sx={{ fontWeight: 900, color: '#0655A5' }}>$</Typography>
                                  </InputAdornment>
                                )
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" sx={elegantInputSx}>
                              <InputLabel>Divisa / Moneda</InputLabel>
                              <Select
                                value={formEdicionMaestra.divisa}
                                label="Divisa / Moneda"
                                onChange={(e) => setFormEdicionMaestra(prev => ({ ...prev, divisa: e.target.value }))}
                              >
                                <MenuItem value="MXN">🇲🇽 MXN (Pesos Mexicanos)</MenuItem>
                                <MenuItem value="USD">🇺🇸 USD (Dólares)</MenuItem>
                                <MenuItem value="EUR">🇪🇺 EUR (Euros)</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Tablero de Impacto en tblpagosacum */}
                          <Grid item xs={12}>
                            {solicitudSeleccionada.acumulado ? (() => {
                              const montoPrev = Number(solicitudSeleccionada.solicitud.MONTO_SOLICITADO || 0);
                              const nuevoMontoNum = Number(formEdicionMaestra.montoSolicitado) || 0;
                              const delta = nuevoMontoNum - montoPrev;
                              const seraCancelada = Number(formEdicionMaestra.estatus) === 6;
                              const restanteOriginal = Number(solicitudSeleccionada.acumulado.RESTANTE || 0);
                              const restanteEstimado = seraCancelada ? (restanteOriginal + montoPrev) : (restanteOriginal - delta);

                              return (
                                <Paper elevation={0} sx={{ p: 1.2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                                  <Typography sx={{ fontSize: '0.54rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase' }}>
                                    Impacto Proyectado en OC {solicitudSeleccionada.solicitud.FOLIO_OC}:
                                  </Typography>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                    <Box>
                                      <Typography sx={{ fontSize: '0.56rem', color: '#64748b' }}>Saldo Restante Proyectado:</Typography>
                                      <Typography sx={{ fontSize: '0.80rem', fontWeight: 900, color: restanteEstimado >= 0 ? '#16a34a' : '#dc2626' }}>
                                        {new Intl.NumberFormat('es-MX', { style: 'currency', currency: formEdicionMaestra.divisa }).format(restanteEstimado)}
                                      </Typography>
                                    </Box>
                                    <Chip
                                      label={seraCancelada ? `Liberará +$${montoPrev.toLocaleString()}` : `${delta >= 0 ? '+' : ''}$${delta.toLocaleString()} ${formEdicionMaestra.divisa}`}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: '0.56rem',
                                        fontWeight: 800,
                                        bgcolor: seraCancelada ? '#dcfce7' : (delta <= 0 ? '#dcfce7' : '#fee2e2'),
                                        color: seraCancelada ? '#15803d' : (delta <= 0 ? '#15803d' : '#991b1b')
                                      }}
                                    />
                                  </Box>
                                </Paper>
                              );
                            })() : (
                              <Alert severity="warning" sx={{ p: 0.5, fontSize: '0.58rem' }}>Sin registro en tblpagosacum</Alert>
                            )}
                          </Grid>
                        </Grid>
                      </Paper>

                      {/* 2. SECCIÓN: SELECTOR VISUAL DE ESTATUS */}
                      <Paper elevation={0} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: '10px', bgcolor: '#ffffff', flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <FactCheckIcon sx={{ color: '#0655A5', fontSize: 22 }} />
                          <Typography sx={{ fontWeight: 900, fontSize: '0.76rem', color: '#0f172a' }}>
                            2. Estatus de la Solicitud (1 Clic)
                          </Typography>
                        </Box>

                        <Grid container spacing={1.2}>
                          {[
                            { val: 1, label: '1. Pendiente', desc: 'En espera de revisión', color: '#64748b', bg: '#f1f5f9' },
                            { val: 2, label: '2. En Proceso', desc: 'Firmas en curso', color: '#0284c7', bg: '#e0f2fe' },
                            { val: 3, label: '3. Autorizado', desc: 'Aprobado para pagar', color: '#16a34a', bg: '#dcfce7' },
                            { val: 4, label: '4. Vencido', desc: 'Confirmar precios', color: '#d97706', bg: '#fef3c7' },
                            { val: 5, label: '5. Pagado', desc: 'Pago completado', color: '#7c3aed', bg: '#ede9fe' },
                            { val: 6, label: '6. Cancelado', desc: 'Cancela y libera saldo', color: '#dc2626', bg: '#fee2e2' }
                          ].map((est) => {
                            const isSelected = Number(formEdicionMaestra.estatus) === est.val;

                            return (
                              <Grid item xs={6} sm={4} key={est.val}>
                                <Paper
                                  onClick={() => setFormEdicionMaestra(prev => ({ ...prev, estatus: est.val }))}
                                  elevation={0}
                                  sx={{
                                    p: 1.2,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    border: isSelected ? `2px solid ${est.color}` : '1px solid #e2e8f0',
                                    bgcolor: isSelected ? est.bg : '#ffffff',
                                    transition: 'all 0.15s ease',
                                    textAlign: 'center',
                                    '&:hover': { borderColor: est.color, transform: 'translateY(-2px)' }
                                  }}
                                >
                                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, color: est.color }}>
                                    {est.label}
                                  </Typography>
                                  <Typography sx={{ fontSize: '0.54rem', color: '#64748b', mt: 0.3, lineHeight: 1.1 }}>
                                    {est.desc}
                                  </Typography>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* ══ COLUMNA DERECHA: FIRMAS, FECHAS Y DATOS GENERALES ══ */}
                    <Grid item xs={12} lg={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* 3. SECCIÓN: ACCIÓN SOBRE FIRMAS */}
                      <Paper elevation={0} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: '10px', bgcolor: '#ffffff' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                          <AssignmentTurnedInIcon sx={{ color: '#0655A5', fontSize: 22 }} />
                          <Typography sx={{ fontWeight: 900, fontSize: '0.76rem', color: '#0f172a' }}>
                            3. Flujo de Firmas y Niveles de Autorización
                          </Typography>
                        </Box>

                        <Stack spacing={1}>
                          {[
                            {
                              val: 'MANTENER',
                              label: '🔄 Mantener Firmas Actuales',
                              desc: 'Conserva el estado de firmas y autorizaciones existentes sin alterarlas.'
                            },
                            {
                              val: 'RESET_FIRMAS',
                              label: '❌ Resetear Firmas a Cero (0)',
                              desc: 'Reinicia el flujo para que los autorizadores vuelvan a firmar desde Nivel 1.'
                            },
                            {
                              val: 'APROBAR_TODAS',
                              label: '✅ Aprobar Todas las Firmas (1, 1, 1, 1)',
                              desc: 'Fuerza la aprobación total automática de todos los niveles inmediatamente.'
                            }
                          ].map((item) => {
                            const isSelected = formEdicionMaestra.accionFirmas === item.val;

                            return (
                              <Paper
                                key={item.val}
                                onClick={() => setFormEdicionMaestra(prev => ({ ...prev, accionFirmas: item.val }))}
                                elevation={0}
                                sx={{
                                  p: 1.2,
                                  borderRadius: '8px',
                                  cursor: 'pointer',
                                  border: isSelected ? '2px solid #0655A5' : '1px solid #e2e8f0',
                                  bgcolor: isSelected ? 'rgba(6, 85, 165, 0.05)' : '#ffffff',
                                  transition: 'all 0.15s ease',
                                  '&:hover': { borderColor: '#0655A5' }
                                }}
                              >
                                <Typography sx={{ fontSize: '0.66rem', fontWeight: 800, color: isSelected ? '#0655A5' : '#1e293b' }}>
                                  {item.label}
                                </Typography>
                                <Typography sx={{ fontSize: '0.54rem', color: '#64748b', mt: 0.2 }}>
                                  {item.desc}
                                </Typography>
                              </Paper>
                            );
                          })}
                        </Stack>
                      </Paper>

                      {/* 4. SECCIÓN: DATOS GENERALES Y FECHAS */}
                      <Paper elevation={0} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: '10px', bgcolor: '#ffffff', flexGrow: 1 }}>
                        <Typography sx={{ fontWeight: 900, fontSize: '0.76rem', color: '#0f172a', mb: 1.5 }}>
                          4. Datos Generales de la Solicitud
                        </Typography>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Fecha de Vencimiento"
                              type="date"
                              value={formEdicionMaestra.fechaVencimiento}
                              onChange={(e) => setFormEdicionMaestra(prev => ({ ...prev, fechaVencimiento: e.target.value }))}
                              fullWidth
                              size="small"
                              InputLabelProps={{ shrink: true }}
                              sx={elegantInputSx}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <TextField
                              label="Centro de Costo ID / CC"
                              value={formEdicionMaestra.centroCostoId}
                              onChange={(e) => setFormEdicionMaestra(prev => ({ ...prev, centroCostoId: e.target.value }))}
                              fullWidth
                              size="small"
                              sx={elegantInputSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="Proveedor"
                              value={formEdicionMaestra.proveedor}
                              onChange={(e) => setFormEdicionMaestra(prev => ({ ...prev, proveedor: e.target.value }))}
                              fullWidth
                              size="small"
                              sx={elegantInputSx}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              label="Observaciones Generales"
                              value={formEdicionMaestra.observaciones}
                              onChange={(e) => setFormEdicionMaestra(prev => ({ ...prev, observaciones: e.target.value }))}
                              fullWidth
                              size="small"
                              multiline
                              rows={1.5}
                              sx={elegantInputSx}
                            />
                          </Grid>
                        </Grid>
                      </Paper>
                    </Grid>

                    {/* ══ FILA INFERIOR: JUSTIFICACIÓN OBLIGATORIA & GUARDADO ══ */}
                    <Grid item xs={12}>
                      <Paper elevation={0} sx={{ p: 2, border: '1px solid #cbd5e1', borderRadius: '10px', bgcolor: '#ffffff', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Paper elevation={0} sx={{ p: 1.2, bgcolor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '7px' }}>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#92400e', mb: 0.5 }}>
                            ⚠️ JUSTIFICACIÓN O MOTIVO DEL CAMBIO (Se registrará con tu usuario en tblbitacora):
                          </Typography>
                          <TextField
                            placeholder="Ej. Ajuste de saldo solicitado por finanzas tras nota de crédito..."
                            value={formEdicionMaestra.justificacion}
                            onChange={(e) => setFormEdicionMaestra(prev => ({ ...prev, justificacion: e.target.value }))}
                            fullWidth
                            size="small"
                            required
                            sx={{ bgcolor: '#ffffff', borderRadius: '6px' }}
                          />
                        </Paper>

                        {/* Botón de Guardado Maestro Destacado */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 0.5 }}>
                          <Button
                            variant="contained"
                            onClick={handleGuardarEdicionMaestra}
                            disabled={guardandoEdicionMaestra}
                            startIcon={guardandoEdicionMaestra ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon sx={{ fontSize: 18 }} />}
                            sx={{
                              background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                              fontWeight: 900,
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontSize: 'clamp(0.70rem, 0.68rem + 0.10vw, 0.82rem)',
                              boxShadow: '0 4px 12px rgba(6, 85, 165, 0.3)',
                              px: 3.5,
                              py: 1.1
                            }}
                          >
                            {guardandoEdicionMaestra ? 'Aplicando Cambios en la Base de Datos...' : 'Guardar y Aplicar Modificación Maestra'}
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* ── CONTENIDO TAB 1: GESTOR COMPLETO DE ARCHIVOS Y DOCUMENTOS ── */}
              {tabMaestro === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1, overflowY: 'auto' }}>
                  {/* Subidor rápido de documentos */}
                  <Paper elevation={0} sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: '8px', bgcolor: '#f8fafc' }}>
                    <Typography sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.06vw, 0.72rem)', fontWeight: 800, color: '#0f172a', mb: 1 }}>
                      📤 Subir Nuevo Documento a esta Solicitud:
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 220, ...elegantInputSx }}>
                        <InputLabel>Tipo de Documento</InputLabel>
                        <Select
                          value={tipoDocSubir}
                          label="Tipo de Documento"
                          onChange={(e) => setTipoDocSubir(e.target.value)}
                        >
                          <MenuItem value="ORDEN_COMPRA">🏷️ Orden de Compra (PDF)</MenuItem>
                          <MenuItem value="FACTURA_PDF">📄 Factura (PDF)</MenuItem>
                          <MenuItem value="FACTURA_XML">📄 Factura (XML)</MenuItem>
                          <MenuItem value="DATOS_BANCARIOS">🏦 Datos Bancarios (Carátula)</MenuItem>
                          <MenuItem value="DOCUMENTACION_ADICIONAL">📎 Documentación Adicional</MenuItem>
                          <MenuItem value="COMPROBANTE_PAGO">💳 Comprobante de Pago</MenuItem>
                        </Select>
                      </FormControl>

                      <Button
                        variant="contained"
                        component="label"
                        disabled={subiendoArchivoControl}
                        startIcon={subiendoArchivoControl ? <CircularProgress size={14} color="inherit" /> : <CloudUploadIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          background: 'linear-gradient(135deg, #0655A5 0%, #0861B8 100%)',
                          textTransform: 'none',
                          fontWeight: 800,
                          fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
                          borderRadius: '6px',
                          boxShadow: 'none',
                          py: 0.8
                        }}
                      >
                        {subiendoArchivoControl ? 'Subiendo...' : 'Seleccionar Archivo y Subir'}
                        <input type="file" hidden onChange={handleSubirArchivoControl} />
                      </Button>
                    </Stack>
                  </Paper>

                  {/* Lista Completa de Documentos Unificados */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 'clamp(0.64rem, 0.62rem + 0.06vw, 0.74rem)', fontWeight: 800, color: '#334155' }}>
                      📁 Archivos y Expediente Digital ({(solicitudSeleccionada.documentos || []).length} archivos disponibles):
                    </Typography>
                    <Typography sx={{ fontSize: '0.58rem', color: '#64748b' }}>
                      Puedes <strong>visualizar</strong>, <strong>reemplazar</strong> por un nuevo archivo o <strong>eliminar</strong> cada documento.
                    </Typography>
                  </Box>

                  {(!solicitudSeleccionada.documentos || solicitudSeleccionada.documentos.length === 0) ? (
                    <Alert severity="info" sx={{ borderRadius: '6px', fontSize: '0.65rem' }}>
                      No se encontraron documentos o adjuntos vinculados a esta solicitud. Usa el botón de arriba para subir uno.
                    </Alert>
                  ) : (
                    <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                      <Table size="small">
                        <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800, fontSize: '0.62rem' }}>Categoría / Tipo</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: '0.62rem' }}>Nombre del Archivo</TableCell>
                            <TableCell sx={{ fontWeight: 800, fontSize: '0.62rem' }}>Detalle / Fecha</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 800, fontSize: '0.62rem', width: 220 }}>Acciones</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {solicitudSeleccionada.documentos.map((doc, idx) => {
                            let catLabel = 'Documentación Adicional';
                            let catColor = '#64748b';
                            let catIcon = '📎';

                            if (doc.categoria === 'ORDEN_COMPRA') {
                              catLabel = 'Orden de Compra';
                              catColor = '#0284c7';
                              catIcon = '🏷️';
                            } else if (doc.categoria === 'FACTURA') {
                              catLabel = doc.tipoDoc ? `Factura (${doc.tipoDoc})` : 'Factura';
                              catColor = '#7c3aed';
                              catIcon = '📄';
                            } else if (doc.categoria === 'DATOS_BANCARIOS') {
                              catLabel = 'Datos Bancarios (Carátula)';
                              catColor = '#059669';
                              catIcon = '🏦';
                            } else if (doc.categoria === 'COMPROBANTE_PAGO') {
                              catLabel = 'Comprobante de Pago';
                              catColor = '#16a34a';
                              catIcon = '💳';
                            }

                            const isReplacing = reemplazandoArchivoId === doc.id;

                            return (
                              <TableRow key={idx} hover sx={{ '&:hover': { bgcolor: 'rgba(6, 85, 165, 0.02)' } }}>
                                <TableCell sx={{ fontSize: '0.60rem', whiteSpace: 'nowrap' }}>
                                  <Chip
                                    label={`${catIcon} ${catLabel}`}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.55rem',
                                      fontWeight: 800,
                                      bgcolor: `${catColor}15`,
                                      color: catColor,
                                      border: `1px solid ${catColor}30`
                                    }}
                                  />
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.62rem', fontWeight: 700, color: '#0f172a', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {doc.nombre}
                                  <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 0.3 }}>
                                    {doc.extension && (
                                      <Chip
                                        label={doc.extension}
                                        size="small"
                                        sx={{ height: 16, fontSize: '0.50rem', fontWeight: 800, bgcolor: '#f1f5f9', color: '#475569' }}
                                      />
                                    )}
                                    {doc.tamano && (
                                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.52rem', fontWeight: 600 }}>
                                        📦 {doc.tamano}
                                      </Typography>
                                    )}
                                    {doc.folioFactura && doc.folioFactura !== 'MANUAL' && (
                                      <Typography variant="caption" sx={{ color: '#0284c7', fontSize: '0.52rem', fontWeight: 700 }}>
                                        Factura: #{doc.folioFactura}
                                      </Typography>
                                    )}
                                  </Stack>
                                  {doc.uuid && (
                                    <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontSize: '0.50rem', mt: 0.2 }}>
                                      UUID: {doc.uuid}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell sx={{ fontSize: '0.58rem', color: '#334155', whiteSpace: 'nowrap' }}>
                                  {doc.monto ? (
                                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: '#16a34a', mb: 0.2 }}>
                                      {new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(doc.monto)}
                                    </Typography>
                                  ) : null}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#475569' }}>
                                      📅 {doc.fecha ? (() => {
                                        let fStr = String(doc.fecha);
                                        if (fStr.includes('T')) fStr = fStr.split('T')[0];
                                        const parts = fStr.split('-');
                                        if (parts.length === 3) {
                                          return `${parts[2]}/${parts[1]}/${parts[0]}`;
                                        }
                                        const d = new Date(doc.fecha);
                                        return !isNaN(d) ? d.toLocaleDateString('es-MX') : fStr;
                                      })() : (solicitudSeleccionada.solicitud?.FECHA_SOLICITUD ? new Date(solicitudSeleccionada.solicitud.FECHA_SOLICITUD).toLocaleDateString('es-MX') : '—')}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Stack direction="row" spacing={0.8} justifyContent="center">
                                    {/* 1. Ver / Descargar */}
                                    <Tooltip title="Ver o Descargar archivo">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => window.open(`${apiUrl}/visualizarPagosAut/${doc.archivoVisualizarId}`, '_blank')}
                                        startIcon={<VisibilityIcon sx={{ fontSize: 13 }} />}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: '0.55rem',
                                          fontWeight: 800,
                                          color: '#0655A5',
                                          borderColor: '#cbd5e1',
                                          py: 0.2,
                                          px: 0.8,
                                          minWidth: 0
                                        }}
                                      >
                                        Ver
                                      </Button>
                                    </Tooltip>

                                    {/* 2. Reemplazar */}
                                    <Tooltip title="Reemplazar con un nuevo archivo">
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        component="label"
                                        disabled={isReplacing}
                                        startIcon={isReplacing ? <CircularProgress size={11} /> : <AutorenewIcon sx={{ fontSize: 13 }} />}
                                        sx={{
                                          textTransform: 'none',
                                          fontSize: '0.55rem',
                                          fontWeight: 800,
                                          color: '#d97706',
                                          borderColor: '#fed7aa',
                                          bgcolor: '#fffbeb',
                                          py: 0.2,
                                          px: 0.8,
                                          minWidth: 0
                                        }}
                                      >
                                        {isReplacing ? 'Cambiando...' : 'Reemplazar'}
                                        <input
                                          type="file"
                                          hidden
                                          onChange={(e) => handleReemplazarArchivoControl(e, doc.tabla, doc.id)}
                                        />
                                      </Button>
                                    </Tooltip>

                                    {/* 3. Eliminar */}
                                    <Tooltip title="Eliminar archivo de la solicitud">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleEliminarArchivoControl(doc.tabla, doc.id)}
                                        sx={{ color: '#ef4444', p: 0.4 }}
                                      >
                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Paper>
                  )}
                </Box>
              )}

              {/* ── CONTENIDO TAB 2: HISTORIAL Y AUDITORÍA INTELIGENTE ── */}
              {tabMaestro === 2 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flexGrow: 1, overflowY: 'auto' }}>
                  {/* 1. Historial de Autorizaciones y Firmas (tblautsolpago_hist) */}
                  <Box>
                    <Typography sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.06vw, 0.72rem)', fontWeight: 800, color: '#0655A5', mb: 0.8 }}>
                      ✍️ Firmas y Flujo de Autorizaciones (tblautsolpago_hist):
                    </Typography>
                    {(!solicitudSeleccionada.historialAut || solicitudSeleccionada.historialAut.length === 0) ? (
                      <Alert severity="info" sx={{ borderRadius: '6px', fontSize: '0.65rem' }}>No hay registros de autorizaciones previas para esta solicitud.</Alert>
                    ) : (
                      <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                        <Table size="small">
                          <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Nivel / Rol</TableCell>
                              <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Usuario Autorizador</TableCell>
                              <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Fecha y Hora</TableCell>
                              <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Estatus Resultante</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {solicitudSeleccionada.historialAut.map((h, idx) => {
                              const fechaH = h.FECHA_EVENTO ? new Date(h.FECHA_EVENTO).toLocaleString('es-MX') : '—';
                              return (
                                <TableRow key={idx} hover>
                                  <TableCell sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#0f172a' }}>
                                    Nivel {h.NIVEL || 1} {h.TIPO ? `(${h.TIPO})` : ''}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#0655A5' }}>
                                    {h.USUARIO_NOMBRE || 'Sistema'}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.58rem', color: '#64748b' }}>
                                    {fechaH}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: '0.58rem' }}>
                                    <Chip
                                      label={h.ESTATUS_NUEVO === 3 ? 'Autorizado' : (h.ESTATUS_NUEVO === 2 ? 'En Proceso' : 'Aprobado')}
                                      size="small"
                                      sx={{ height: 18, fontSize: '0.54rem', fontWeight: 800, bgcolor: '#dcfce7', color: '#15803d' }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Paper>
                    )}
                  </Box>

                  {/* 2. Registros de Bitácora de Eventos con Filtro Específico */}
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                      <Box>
                        <Typography sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.06vw, 0.72rem)', fontWeight: 800, color: '#334155' }}>
                          📜 Bitácora de Auditoría y Eventos (tblbitacora):
                        </Typography>
                        <Typography sx={{ fontSize: '0.56rem', color: '#64748b' }}>
                          Filtrar eventos para distinguir entre esta solicitud y otros movimientos de la OC {solicitudSeleccionada.solicitud.FOLIO_OC}
                        </Typography>
                      </Box>

                      {/* Selector de Filtro de Bitácora */}
                      <Stack direction="row" spacing={0.8}>
                        <Button
                          size="small"
                          variant={filtroBitacoraModo === 'ESTA_SOLICITUD' ? 'contained' : 'outlined'}
                          onClick={() => setFiltroBitacoraModo('ESTA_SOLICITUD')}
                          sx={{
                            fontSize: '0.58rem',
                            fontWeight: 800,
                            textTransform: 'none',
                            borderRadius: '5px',
                            py: 0.3,
                            bgcolor: filtroBitacoraModo === 'ESTA_SOLICITUD' ? '#0655A5' : 'transparent',
                            color: filtroBitacoraModo === 'ESTA_SOLICITUD' ? '#ffffff' : '#475569',
                            borderColor: '#cbd5e1'
                          }}
                        >
                          🎯 Solo de esta Solicitud (#{solicitudSeleccionada.solicitud.AUTSOLPAGOID})
                        </Button>
                        <Button
                          size="small"
                          variant={filtroBitacoraModo === 'TODOS_OC' ? 'contained' : 'outlined'}
                          onClick={() => setFiltroBitacoraModo('TODOS_OC')}
                          sx={{
                            fontSize: '0.58rem',
                            fontWeight: 800,
                            textTransform: 'none',
                            borderRadius: '5px',
                            py: 0.3,
                            bgcolor: filtroBitacoraModo === 'TODOS_OC' ? '#0655A5' : 'transparent',
                            color: filtroBitacoraModo === 'TODOS_OC' ? '#ffffff' : '#475569',
                            borderColor: '#cbd5e1'
                          }}
                        >
                          🌐 Todos los eventos de la OC ({solicitudSeleccionada.solicitud.FOLIO_OC})
                        </Button>
                      </Stack>
                    </Box>

                    {(() => {
                      const listaFiltrada = (solicitudSeleccionada.bitacora || []).filter((bit) => {
                        if (filtroBitacoraModo === 'ESTA_SOLICITUD') {
                          return bit.esDeEstaSolicitud;
                        }
                        return true;
                      });

                      if (listaFiltrada.length === 0) {
                        return (
                          <Alert severity="info" sx={{ borderRadius: '6px', fontSize: '0.65rem' }}>
                            {filtroBitacoraModo === 'ESTA_SOLICITUD'
                              ? `No hay registros específicos de creación/edición para la Solicitud #${solicitudSeleccionada.solicitud.AUTSOLPAGOID}. Prueba seleccionando "Todos los eventos de la OC".`
                              : `No hay registros de bitácora vinculados a la OC ${solicitudSeleccionada.solicitud.FOLIO_OC}.`}
                          </Alert>
                        );
                      }

                      return (
                        <Paper sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                          <Table size="small">
                            <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Origen / Alcance</TableCell>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Fecha y Hora</TableCell>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Usuario</TableCell>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Acción</TableCell>
                                <TableCell sx={{ fontWeight: 800, fontSize: '0.60rem' }}>Detalle / Registro</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {listaFiltrada.map((bit) => {
                                let fechaLimpia = bit.fecha || '';
                                if (fechaLimpia.includes('T')) {
                                  const d = new Date(fechaLimpia);
                                  fechaLimpia = !isNaN(d) ? d.toLocaleDateString('es-MX') : fechaLimpia.split('T')[0];
                                }
                                const horaLimpia = bit.hora ? bit.hora.slice(0, 5) : '';

                                return (
                                  <TableRow key={bit.id_bitacora} hover sx={{ bgcolor: bit.esDeEstaSolicitud ? 'rgba(6, 85, 165, 0.02)' : 'transparent' }}>
                                    <TableCell sx={{ fontSize: '0.58rem', whiteSpace: 'nowrap' }}>
                                      <Chip
                                        label={bit.etiquetaOrigen || (bit.esDeEstaSolicitud ? `🎯 Solicitud #${solicitudSeleccionada.solicitud.AUTSOLPAGOID}` : `OC ${solicitudSeleccionada.solicitud.FOLIO_OC} (General)`)}
                                        size="small"
                                        sx={{
                                          height: 20,
                                          fontSize: '0.54rem',
                                          fontWeight: 800,
                                          bgcolor: bit.esDeEstaSolicitud ? '#dbeafe' : '#f1f5f9',
                                          color: bit.esDeEstaSolicitud ? '#1e40af' : '#64748b',
                                          border: bit.esDeEstaSolicitud ? '1px solid #bfdbfe' : '1px solid #e2e8f0'
                                        }}
                                      />
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.58rem', color: '#64748b', whiteSpace: 'nowrap' }}>
                                      {fechaLimpia} {horaLimpia}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#0655A5' }}>
                                      {bit.usuario}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.58rem', fontWeight: 700 }}>
                                      {bit.accion}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.58rem', color: '#334155' }}>
                                      {bit.valor_nuevo || bit.valor_anterior}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </Paper>
                      );
                    })()}
                  </Box>
                </Box>
              )}
            </Paper>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            variant="outlined"
            onClick={() => setModalControlMaestroOpen(false)}
            sx={{
              textTransform: 'none',
              color: '#475569',
              borderColor: '#cbd5e1',
              fontWeight: 700,
              fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
              borderRadius: '6px',
              px: 2,
              '&:hover': { borderColor: '#94a3b8', bgcolor: '#f1f5f9' }
            }}
          >
            Cerrar Panel Maestro
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── MODAL CONTROL Y SINCRONIZACIÓN DE BASE DE DATOS ESPEJO ── */}
      <Dialog
        open={modalEspejoOpen}
        onClose={() => !sincronizandoEspejo && setModalEspejoOpen(false)}
        maxWidth="md"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
            color: '#ffffff',
            px: 'clamp(12px, 1.5vw, 18px)',
            py: 'clamp(8px, 1vw, 12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DnsIcon sx={{ fontSize: 'clamp(18px, 1.6vw, 22px)' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 'clamp(0.82rem, 0.78rem + 0.18vw, 0.98rem)' }}>
                Base de Datos Espejo y Alta Disponibilidad
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.90)', fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.68rem)' }}>
                Replicación en tiempo real y conmutación automática por error (Failover)
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setModalEspejoOpen(false)} sx={{ color: '#ffffff', p: 0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 'clamp(12px, 1.5vw, 18px)', bgcolor: '#f8fafc' }}>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {/* Panel de Servidores */}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    bgcolor: '#ffffff'
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.70rem, 0.68rem + 0.08vw, 0.80rem)', color: '#0f172a' }}>
                      🖥️ Servidor Principal
                    </Typography>
                    <Chip
                      label={estadoEspejo?.status?.primary?.available ? 'Activo (En Línea)' : 'Desconectado'}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        bgcolor: estadoEspejo?.status?.primary?.available ? '#dcfce7' : '#fee2e2',
                        color: estadoEspejo?.status?.primary?.available ? '#15803d' : '#b91c1c',
                        fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)',
                        height: 18
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.70rem)', color: '#475569' }}>
                    <strong>Host:</strong> {estadoEspejo?.status?.primary?.host || '172.16.112.210'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.70rem)', color: '#475569' }}>
                    <strong>Base de Datos:</strong> {estadoEspejo?.status?.primary?.database || 'PagosEseasa'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.70rem)', color: '#475569' }}>
                    <strong>Tablas Base:</strong> {estadoEspejo?.tablasPrincipal ?? 47} tablas
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    bgcolor: '#ffffff'
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.8 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.70rem, 0.68rem + 0.08vw, 0.80rem)', color: '#0f172a' }}>
                      🪞 Servidor Espejo
                    </Typography>
                    <Chip
                      label={estadoEspejo?.mirrorConectado ? 'Conectado y Listo' : 'No Conectado'}
                      size="small"
                      sx={{
                        fontWeight: 800,
                        bgcolor: estadoEspejo?.mirrorConectado ? '#dcfce7' : '#fee2e2',
                        color: estadoEspejo?.mirrorConectado ? '#15803d' : '#b91c1c',
                        fontSize: 'clamp(0.56rem, 0.54rem + 0.05vw, 0.64rem)',
                        height: 18
                      }}
                    />
                  </Stack>
                  <Typography variant="body2" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.70rem)', color: '#475569' }}>
                    <strong>Host:</strong> {estadoEspejo?.status?.mirror?.host || '10.10.30.5'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.70rem)', color: '#475569' }}>
                    <strong>Base de Datos:</strong> {estadoEspejo?.status?.mirror?.database || 'PagosEseasa'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.70rem)', color: '#475569' }}>
                    <strong>Tablas Base:</strong> {estadoEspejo?.tablasEspejo ?? 47} tablas
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Estado de Paridad */}
            <Alert
              severity={estadoEspejo?.paridadExacta ? 'success' : 'warning'}
              icon={estadoEspejo?.paridadExacta ? <CheckCircleIcon /> : <WarningAmberIcon />}
              sx={{
                borderRadius: '8px',
                fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.75rem)',
                py: 0.5
              }}
            >
              {estadoEspejo?.paridadExacta
                ? '✅ ¡Paridad Total! Todas las 47 tablas existen en ambos servidores. La réplica automática de escrituras está activa.'
                : '⚠️ Se detectaron diferencias en tablas entre los dos servidores. Puedes ejecutar una sincronización de puesta al día a continuación.'}
            </Alert>

            {/* Resultado de la última sincronización si hubo */}
            {resultadoSyncEspejo && (
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: '8px',
                  bgcolor: '#f0fdf4',
                  border: '1px solid #bbf7d0'
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 'clamp(0.70rem, 0.68rem + 0.08vw, 0.80rem)', color: '#166534', mb: 0.5 }}>
                  🎉 Resultado de la Sincronización ({resultadoSyncEspejo.fechaSincronizacion}):
                </Typography>
                <Typography variant="body2" sx={{ fontSize: 'clamp(0.62rem, 0.60rem + 0.05vw, 0.70rem)', color: '#15803d' }}>
                  • {resultadoSyncEspejo.mensaje}
                </Typography>
                {resultadoSyncEspejo.detalles?.length > 0 && (
                  <Box sx={{ mt: 1, maxHeight: 160, overflowY: 'auto' }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#dcfce7' }}>
                          <TableCell sx={{ fontWeight: 800, fontSize: '0.65rem' }}>Tabla</TableCell>
                          <TableCell sx={{ fontWeight: 800, fontSize: '0.65rem', textAlign: 'right' }}>Registros Replicados</TableCell>
                          <TableCell sx={{ fontWeight: 800, fontSize: '0.65rem', textAlign: 'right' }}>Diferencia Previa</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {resultadoSyncEspejo.detalles.map((d, i) => (
                          <TableRow key={i}>
                            <TableCell sx={{ fontSize: '0.65rem', fontFamily: 'monospace' }}>{d.tabla}</TableCell>
                            <TableCell sx={{ fontSize: '0.65rem', textAlign: 'right' }}>{d.registros?.toLocaleString()}</TableCell>
                            <TableCell sx={{ fontSize: '0.65rem', textAlign: 'right', color: '#dc2626', fontWeight: 700 }}>+{d.diferenciaPrevia}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </Paper>
            )}

            {/* Información Técnica del Mecanismo */}
            <Paper
              elevation={0}
              sx={{
                p: 1.2,
                borderRadius: '8px',
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0'
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 'clamp(0.65rem, 0.62rem + 0.06vw, 0.72rem)', color: '#334155' }}>
                ℹ️ ¿Cómo funciona la Alta Disponibilidad?
              </Typography>
              <Typography variant="body2" sx={{ fontSize: 'clamp(0.58rem, 0.56rem + 0.05vw, 0.66rem)', color: '#64748b', mt: 0.3 }}>
                1. <strong>Doble Escritura Activa:</strong> Cada orden creada, aprobada o pagada se guarda en el Principal y se replica al Espejo.<br />
                2. <strong>Failover Automático:</strong> Si el Principal se apaga, el sistema conmuta al Espejo en milisegundos sin tirar el sistema.<br />
                3. <strong>Auto-Recuperación:</strong> Cuando el Principal regresa, el sistema le inyecta automáticamente los cambios acumulados durante la caída.
              </Typography>
            </Paper>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 'clamp(8px, 1vw, 12px)', bgcolor: '#ffffff', borderTop: '1px solid #e2e8f0', justifyContent: 'space-between' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={handleConsultarEstadoEspejo}
            disabled={loadingEspejo || sincronizandoEspejo}
            startIcon={loadingEspejo ? <CircularProgress size={12} /> : <RefreshIcon sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
              color: '#475569',
              borderColor: '#cbd5e1'
            }}
          >
            Refrescar Estado
          </Button>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setModalEspejoOpen(false)}
              disabled={sincronizandoEspejo}
              sx={{
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
                color: '#64748b',
                borderColor: '#cbd5e1'
              }}
            >
              Cerrar
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleEjecutarSincronizacionEspejo}
              disabled={sincronizandoEspejo}
              startIcon={sincronizandoEspejo ? <CircularProgress size={13} color="inherit" /> : <SyncAltIcon sx={{ fontSize: 15 }} />}
              sx={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                color: '#ffffff',
                fontWeight: 800,
                borderRadius: '6px',
                textTransform: 'none',
                fontSize: 'clamp(0.60rem, 0.58rem + 0.05vw, 0.70rem)',
                boxShadow: 'none',
                '&:hover': { background: 'linear-gradient(135deg, #047857 0%, #059669 100%)' }
              }}
            >
              {sincronizandoEspejo ? 'Sincronizando...' : 'Sincronizar Puesta al Día Ahora'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

